import { CommonModule } from '@angular/common';
import saveAs from 'file-saver';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Menu } from 'primeng/menu';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { BaseComponent } from '../../../../@core/components/base/base.component';
import { ButtonComponent } from '../../../../@core/components/ui/button/button.component';
import { DialogComponent } from '../../../../@core/components/ui/dialog/dialog.component';
import { IconComponent, IconSourceEnum } from '../../../../@core/components/ui/icon/icon.component';
import { ToggleComponent } from '../../../../@core/components/ui/toggle/toggle.component';
import { SearchBarComponent } from '../../../../@shared/components/search-bar/search-bar.component';
import { AvatarStackComponent } from '../../../../@shared/components/avatar-stack/avatar-stack.component';
import { StepperComponent } from '../../../../@shared/components/stepper/stepper.component';
import { StepperStepDirective } from '../../../../@shared/components/stepper-layout/stepper-step.directive';
import { StepperConfig, StepperStepConfig } from '../../../../@shared/components/stepper-layout/stepper.types';
import { StepperService } from '../../../../@shared/components/stepper-layout/stepper.service';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { MenuComponent, CustomMenuItem, MenuItemType } from '../../../../@core/components/ui/menu/menu.component';
import { IUser, IProject, IMilestone, MILESTONE_TYPE, IShareUser, SHARE_ACCESS, CONTRACT_USER_ROLE } from '../../../../@core/models/model';
import { GEOTECHNICAL_ANALYSIS_SVG, PROJECT_DASHBOARD_SVG } from 'src/app/common/svgs';
import { FileSelectionComponent } from '../../../../@shared/components/file-selection/file-selection.component';
import {
    FileSelectionConfig,
    FileItem,
    FileSelectionChangeEvent,
    FileSelectionSubmitEvent,
    FetchFunction,
    DropdownOption,
    TabType,
} from '../../../../@shared/components/file-selection/file-selection.model';
import { FileDropTypes } from '../../../../@shared/components/file-drop/file-drop.types';
import { KnowledgeBaseService } from '../../../../contract-management/services/knowledgebase.service';
import { UserService } from '../../../../contract-management/services/user.service';
import { RepositoryService } from '../../../../contract-management/services/repository.service';
import { ProjectService } from '../../../../contract-management/services/project.service';
import { ContractService } from '../../../../contract-management/services/contracts.service';
import { MileStoneService } from '../../../../contract-management/services/milestone.service';
import {
    GeotechnicalAnalysisService,
    GeotechnicalAnalysisListItem,
    GeotechnicalAnalysisListFilters,
    GeotechnicalAnalysisReport,
    GeneratedAnalysisExcelListItem,
    GetAnalysisFileResponse,
    GeotechnicalUploadFile,
    GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE,
    GEOTECHNICAL_CONFIGURATION_TYPE,
    UserGeotechnicalPreference,
    GeoPreferenceInputRef,
} from '../../services/geotechnical-analysis.service';
import { ToastService } from '../../../../@core/services/toast.service';
import { AuthenticationService } from '../../../../@core/services/authentication.service';
import { CONTRACT_TYPE, DOCUMENT_MODEL, IContract } from '../../../../contract-management/models/contract-management.model';
import { KNOWLEDGE_SCOPE } from '../../../../contract-management/models/contract-management.model';
import { ChatAttachment } from '../../../general-agent/models/types';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { FileIconComponent } from '../../../../@shared/components/file-icon/file-icon.component';
import { FILE_TYPE, USER_TYPE, ONLY_OFFICE_EDITOR_TYPE, USER_ROLE } from '../../../../@core/models/enums';
import { OnlyOfficeDocEditorComponent } from '../../../../@shared/components/only-office-doc-editor/only-office-doc-editor.component';
import { IConfig } from '@onlyoffice/document-editor-angular';
import { JWTService } from '../../../../contract-management/services/jwt.service';
import { environment } from '../../../../../environments/environment.dev';
import { TabsModule } from 'primeng/tabs';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectComponent } from '../../../../@core/components/ui/select/select.component';
import { MultiSelectComponent } from '../../../../@core/components/ui/multi-select/multi-select.component';
import { SelectOption } from '../../../../@core/components/ui/select/select.model';
import { DatepickerComponent } from '../../../../@core/components/ui/datepicker/datepicker.component';
import { InputTextModule } from 'primeng/inputtext';
import { MilestonesComponent } from '../../../../contract-management/components/milestones/milestones.component';
import { ShareToUsersComponent } from '../../../../@shared/components/share-to-users/share-to-users.component';
import { GridListViewComponent } from '../../../../@shared/components/grid-list-view/grid-list-view.component';
import { SelectButtonComponent } from '../../../../@core/components/ui/select-button/select-button.component';
import { PopoverComponent } from '../../../../@core/components/ui/popover/popover.component';
import { SegmentedOption } from '../../../../@core/components/ui/segmented/segmented.component';
import { TableColumn, TableConfig } from '../../../../@shared/models/table.models';
import {
    GEOTECHNICAL_ANALYSIS_TABLE_COLUMNS,
    GEOTECHNICAL_ANALYSIS_TABLE_CONFIG,
} from './geotechnical-analysis-table.config';
import moment from 'moment';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { Router } from '@angular/router';
import { DeleteConfirmationComponent } from '../../../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { AssetUrlPipe } from "../../../../@shared/pipes/asset-url.pipe";
import { SocketService, SOCKET_EVENTS } from '../../../../@core/services/socket.service';
import { AttachmentGroupComponent } from "src/app/@shared/components/attachment-group/attachment-group.component";
import { AttachmentFilterChip } from 'src/app/@shared/components/attachment-group/attachment-group-filter-types';
import { IntegrationsService, MailMessageInfo } from '../../../../contract-management/services/integrations.service';
import { CONNECTOR_PROVIDER_INFO, TOOLKIT_PROVIDERS } from 'src/app/features/general-agent/constants/tool.constants';
import { UserCreditsStateService } from 'src/app/features/credits/services/user-credits-state.service';

/** Item shown in the "Generate analysis excel" sidebar (from GET /generate-analysis-excel list). */
export interface GenerateExcelAgreementItem {
    id: string;
    name: string;
    selected: boolean;
    fileCount: number;
    hasPdf: boolean;
    hasDoc: boolean;
    hasExcel: boolean;
    /** Backend list item for useForFutureAnalysis, documents, etc. */
    source?: GeneratedAnalysisExcelListItem;
}

export interface GeotechnicalAnalysisItem {
    id: string;
    reportName: string;
    projectName: string;
    creatorName: string;
    createdAt?: string; // ISO for sorting/display
    dueDate?: string;   // ISO for sorting/display
    milestoneName: string;
    milestoneDate: string;
    assignedUsers: (IUser | Partial<IUser>)[];
    lastEditedOn: string;
    lastEditedBy: string;
    status: string;
    currentUserRole: string;
}

/** Payload for "Lay the foundation" step: reports (multiple), formulae (single), analysisExcel (single) as ArrayBuffers */
export interface LayFoundationPayload {
    reports: ArrayBuffer[];
    formulae: ArrayBuffer | null;
    analysisExcel: ArrayBuffer | null;
}

export type LayFoundationFileContext = 'reports' | 'formulae' | 'analysisExcel' | 'generateAnalysisExcel';

/** Display item for a selected file (from repository/kb or local) */
export interface LayFoundationFileDisplay {
    id: string;
    name: string;
    size?: number;
    sourceLabel: string; // 'Knowledge base' | 'Project file' | 'Local file' | 'Documents'
    isLocal: boolean;
    file?: File;
    fileItem?: FileItem;
}

/** Backend expects refs with source + optional _id (for non-local). Source: 'local' | 'knowledgeBase' | 'project' | 'documents' | 'integration' */
export interface GeotechnicalInputRef {
    source: string;
    file?: string;
    attachment?: string;
    connector?: string;
    messageId?: string;
    attachmentId?: string;
    fileId?: string;
    path?: string;
    serverRelativeUrl?: string;
    siteUrl?: string;
}

/** Payload for POST body (JSON). Matches backend SaveBody shape. */
export interface GeotechnicalSaveDataPayload {
    reports: GeotechnicalInputRef[];
    formulae?: GeotechnicalInputRef | null;
    analysisExcel?: GeotechnicalInputRef | null;
    /** Multiple analysis excel refs (generate-analysis-excel multi-file flow). */
    analysisExcels?: GeotechnicalInputRef[] | null;
    name?: string | null;
    project?: string | null;
    dueDate?: string | null;
    milestones?: (Partial<IMilestone> & { reminder?: unknown })[];
    deletedMilestoneIds?: string[];
    teamMembers?: IShareUser[];
    workspaceShare?: boolean;
    projectShare?: boolean;
    /** S3 keys for locally uploaded files (pre-signed URL flow). */
    reportFileKeys?: string[];
    formulaFileKey?: string | null;
    analysisExcelFileKey?: string | null;
    /** When true, backend saves formulae as the user's default. */
    setFormulaeAsDefault?: boolean;
    /** When true, backend saves analysis excel as the user's default. */
    setAnalysisExcelAsDefault?: boolean;
    /** When true, analysis excel was pre-populated from user default — backend will copy the attachment before saving. */
    analysisExcelIsFromDefault?: boolean;
    /** When true, backend will clear the user's default formulae preference. */
    clearFormulaeDefault?: boolean;
    /** When true, backend will clear the user's default analysis excel preference. */
    clearAnalysisExcelDefault?: boolean;
    projectInfo?: {
        projectArea: number | null;
        shiftHoursPerDay: number | null;
        theoreticalBudget: number | null;
        theoreticalScheduleWorkdays: number | null;
        projectLocation?: string | null;
    } | null;
}

const ALL_KNOWLEDGE_FOLDER_PREFIX = 'all-knowledge-';
const GENERATED_EXCEL_TAB_ID = 'GENERATED_EXCEL';

const INTEGRATIONS_TAB_ID = 'INTEGRATIONS';
const INTEGRATIONS_TOOLKIT_PROVIDERS = [
    TOOLKIT_PROVIDERS.GOOGLE_DRIVE,
    TOOLKIT_PROVIDERS.ONE_DRIVE,
    TOOLKIT_PROVIDERS.SHAREPOINT,
    TOOLKIT_PROVIDERS.DROPBOX,
    TOOLKIT_PROVIDERS.OUTLOOK,
] as const;

@Component({
    selector: 'app-geotechnical-analysis',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonComponent,
        IconComponent,
        ToggleComponent,
        SearchBarComponent,
        AvatarStackComponent,
        StepperComponent,
        StepperStepDirective,
        MenuComponent,
        AvatarModule,
        AvatarGroupModule,
        MenuModule,
        FileSelectionComponent,
        TooltipModule,
        AccordionModule,
        FileIconComponent,
        TabsModule,
        ProgressSpinnerModule,
        SelectComponent,
        MultiSelectComponent,
        DatepickerComponent,
        InputTextModule,
        MilestonesComponent,
        ShareToUsersComponent,
        GridListViewComponent,
        SelectButtonComponent,
        PopoverComponent,
        DrawerModule,
        DialogModule,
        DeleteConfirmationComponent,
        AssetUrlPipe,
        DialogComponent,
        AttachmentGroupComponent,
        OnlyOfficeDocEditorComponent
    ],
    templateUrl: './geotechnical-analysis.component.html',
    styleUrl: './geotechnical-analysis.component.scss',
    providers: [StepperService]
})
export class GeotechnicalAnalysisComponent extends BaseComponent implements OnInit, OnDestroy {

    private stepperService = inject(StepperService);
    private userService = inject(UserService);
    private knowledgeBaseService = inject(KnowledgeBaseService);
    private repositoryService = inject(RepositoryService);
    private projectService = inject(ProjectService);
    private contractService = inject(ContractService);
    private geotechnicalAnalysisService = inject(GeotechnicalAnalysisService);
    private toastService = inject(ToastService);
    private authService = inject(AuthenticationService);
    private socketService = inject(SocketService);
    private integrationsService = inject(IntegrationsService);
    private userCreditsState = inject(UserCreditsStateService);
    private jwtService = new JWTService();

    private readonly manageConnectorsMenuItem: CustomMenuItem = {
        label: 'Manage Connectors',
        value: 'manage-connectors',
        icon: 'settings-2',
        type: MenuItemType.SINGLE_SELECT,
        iconSource: IconSourceEnum.LUCIDE,
        subOptions: [],
        buttonCallback: () => {
            this.router.navigate(['/settings/integrations']);
        },
    };
    integrationsConnectorsMenuItems: CustomMenuItem[] = [this.manageConnectorsMenuItem];
    integrationPaginationState: { [key: string]: { nextPageToken?: string; nextCursor?: string } } = {};
    private outlookMailsCache = new Map<string, MailMessageInfo>();

    searchAnalysisName = '';
    analysisItems: GeotechnicalAnalysisItem[] = [];
    totalRecords = 0;
    loadingAnalyses = signal(false);
    cardMenuItems: CustomMenuItem[] = [];
    selectedCardItem: GeotechnicalAnalysisItem | null = null;
    showDeleteDialog = false;
    deletingAnalysis = false;
    /** Card rename dialog state. */
    renameCardDialogVisible = false;
    renameCardValue = '';
    renamingCard = false;
    /** Card set-due-date dialog state. */
    cardDueDateDialogVisible = false;
    cardDueDateValue: Date | null = null;
    settingCardDueDate = false;
    private currentlyOpenCardMenu: MenuComponent | null = null;

    showFilterPanel = false;
    filterProject: IProject | null = null;

    // ── Configurations drawer ─────────────────────────────────────────────────
    /** Whether the configurations right-side drawer is open. */
    showConfigurationsDrawer = false;
    /** Loaded AI Dashboard Instructions value from the backend. */
    aiDashboardInstructions = signal<string>('');
    /** Whether the AI Dashboard Instructions text-area is in edit mode. */
    aiDashboardEditing = signal<boolean>(false);
    /** Scratch value while editing (not persisted until Save is clicked). */
    aiDashboardDraft = signal<string>('');
    /** True while a save request is in flight. */
    aiDashboardSaving = signal<boolean>(false);
    /** Loaded Summary Instructions value from the backend. */
    summaryInstructions = signal<string>('');
    /** Whether the Summary Instructions text-area is in edit mode. */
    summaryEditing = signal<boolean>(false);
    /** Scratch value while editing Summary Instructions. */
    summaryDraft = signal<string>('');
    /** True while a Summary Instructions save is in flight. */
    summarySaving = signal<boolean>(false);
    /** Whether layer visualization thicknesses should be rounded to the nearest integer, workspace-wide. */
    roundLayerThickness = signal<boolean>(false);
    /** True while the round-layer-thickness toggle save request is in flight. */
    roundLayerThicknessSaving = signal<boolean>(false);
    filterCreatedBy: string[] = [];
    filterDueDateRange: Date[] | null = null;
    filterDateCreatedRange: Date[] | null = null;
    createdByOptions: SelectOption[] = [];

    viewMode: 'grid' | 'list' = 'grid';
    viewModeOptions: SegmentedOption[] = [
        { label: 'List', value: 'list', icon: 'pi pi-bars' },
        { label: 'Grid', value: 'grid', icon: 'pi pi-th-large' },
    ];
    defaultPaginatorValues = { first: 0, rows: 20 };
    first: number = this.defaultPaginatorValues.first;
    rows: number = this.defaultPaginatorValues.rows;
    tableColumns: TableColumn[] = [...GEOTECHNICAL_ANALYSIS_TABLE_COLUMNS];
    tableConfig: TableConfig = { ...GEOTECHNICAL_ANALYSIS_TABLE_CONFIG };

    @ViewChild('reportNameTemplateRef') reportNameTemplateRef!: TemplateRef<unknown>;
    @ViewChild('projectNameTemplateRef') projectNameTemplateRef!: TemplateRef<unknown>;
    @ViewChild('creatorNameTemplateRef') creatorNameTemplateRef!: TemplateRef<unknown>;
    @ViewChild('createdDateTemplateRef') createdDateTemplateRef!: TemplateRef<unknown>;
    @ViewChild('statusTemplateRef') statusTemplateRef!: TemplateRef<unknown>;
    @ViewChild('upcomingMilestoneTemplateRef') upcomingMilestoneTemplateRef!: TemplateRef<unknown>;
    @ViewChild('dueDateTemplateRef') dueDateTemplateRef!: TemplateRef<unknown>;
    @ViewChild('actionsTemplateRef') actionsTemplateRef!: TemplateRef<unknown>;
    @ViewChild('sortOverlay') sortOverlay!: PopoverComponent;


    showGenerateAnalysisStepper = false;
    isEditMode = false;
    editingAnalysisId: string | null = null;
    showUnsavedChangesDialog = false;
    private pendingUnsavedChangesAction: (() => void) | null = null;
    stepperConfig = signal<StepperConfig>(this.createAnalysisStepperConfig());
    GEOTECHNICAL_ANALYSIS_SVG = GEOTECHNICAL_ANALYSIS_SVG;
    @ViewChild('cardMenu') cardMenu!: Menu;
    @ViewChild('agreementKebabMenu') agreementKebabMenu!: Menu;

    // Lay the foundation (Step 1) state
    reportsFiles: LayFoundationFileDisplay[] = [];
    formulaeFile: LayFoundationFileDisplay | null = null;
    analysisExcelFile: LayFoundationFileDisplay | null = null;
    /** Whether to save the current formulae file as the user's default. */
    setFormulaeAsDefault = false;
    /** Whether to save the current analysis excel file as the user's default. */
    setAnalysisExcelAsDefault = false;
    /** Attachment ID of the user's current default analysis excel (used for sidebar toggle comparison). */
    defaultAnalysisExcelAttachmentId: string | null = null;
    /** True when the current analysis excel was pre-populated from the user's default (triggers a copy on save). */
    analysisExcelIsFromDefault = false;
    /** True when the analysis excel was originally pre-populated from default (persists even after file change; used to compute clearAnalysisExcelDefault). */
    private analysisExcelPrePopulatedFromDefault = false;
    /** True when the formulae was originally pre-populated from default (used to compute clearFormulaeDefault). */
    private formulaePrePopulatedFromDefault = false;
    openFileSelectionDialog = false;
    fileSelectionContext: LayFoundationFileContext | null = null;

    /** "Generate analysis excel" sidebar: agreements list with toggles. */
    showGenerateExcelSidebar = false;
    generateExcelAgreementItems: GenerateExcelAgreementItem[] = [];
    loadingGenerateExcelAgreements = false;
    private pendingPreferenceUpdate$: Observable<{ success?: boolean } | null> | null = null;
    generateExcelAgreementMenuItems: MenuItem[] = [];
    agreementRowMenuItems: CustomMenuItem[] = [
        { label: 'View', icon: 'pi pi-eye' },
        { label: 'Rename', icon: 'pi pi-pencil' },
        { label: 'Delete', icon: 'pi pi-trash', class: 'text-red-500' },
    ];
    selectedAgreementMenuItem: GenerateExcelAgreementItem | null = null;
    /** Sidebar sub-view: 'list' = generated excels list, 'files' = See all files for one item. */
    generateExcelSidebarView: 'list' | 'files' = 'list';
    seeAllFilesSelectedItem: GenerateExcelAgreementItem | null = null;
    seeAllFilesSearchQuery = '';
    /** "Uploaded files" dialog visibility (opened from "See all" on agreement row). */
    uploadedFilesDialogVisible = false;
    /** Rename agreement dialog state. */
    renameAgreementDialogVisible = false;
    renameAgreementValue = '';
    renamingAgreement = false;
    private renameAgreementTarget: GenerateExcelAgreementItem | null = null;
    /** Delete agreement confirmation dialog state. */
    deleteAgreementDialogVisible = false;
    deletingAgreement = false;
    private deleteAgreementTarget: GenerateExcelAgreementItem | null = null;
    /** Analysis excel preview dialog state. */
    analysisExcelPreviewVisible = false;
    analysisExcelPreview: GenerateExcelAgreementItem | null = null;
    analysisExcelEditor: IConfig = {};
    readonly uploadedFilesDialogStyle = { width: '90vw', maxWidth: '56rem' };
    readonly uploadedFilesDialogContentStyle = { overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '70vh' };
    fileSelectionConfig = signal<FileSelectionConfig>(this.getDefaultFileSelectionConfig());
    fileSelectionDropdownOptions = signal<DropdownOption[]>([]);
    knowledgeBases: { _id: string; name: string; files?: { file: { _id: string; name: string; size?: number } }[] }[] = [];
    projects: { _id: string; name: string }[] = [];

    readonly FORMULAE_TOOLTIP = 'To ensure accurate analysis, please upload one consolidated document (DOCX or PDF) containing all formulae images relevant to this analysis';
    readonly ANALYSIS_EXCEL_TOOLTIP = 'To ensure accurate analysis, review the Instructions tab in the standard analysis Excel and customize the analysis as per your requirements';
    readonly PROJECT_AREA_TOOLTIP = 'The total area of work considered for the project\n\nNote: Provide the project area in Square Yards (SY)';
    readonly SHIFT_HOURS_TOOLTIP = 'The number of shift hours scheduled in a standard working day, used for labor and equipment productivity calculations';
    readonly THEORETICAL_BUDGET_TOOLTIP = 'The baseline budget assumed to be required to complete the project';
    readonly THEORETICAL_SCHEDULE_TOOLTIP = 'The baseline duration in workdays assumed to be required to complete the project';
    readonly PROJECT_LOCATION_TOOLTIP = 'Geographical location of the project';
    downloadingSample = false;
    GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE = GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE;

    /** Track current step index for validation (e.g. disable Next on step 0 when incomplete) */
    currentStepIndex = signal(0);

    /** True while create-and-save request is in progress */
    savingAnalysis = signal(false);

    /** Separate accordion open state: '0' = expanded, undefined/null = collapsed. All expanded by default. */
    reportsAccordionValue: string | undefined = '0';
    formulaeAccordionValue: string | undefined = '0';
    analysisExcelAccordionValue: string | undefined = '0';

    // Step 2 - Analysis information state (two separate accordions; customisation expanded by default)
    analysisInfoCustomisationAccordionValue: string | undefined = '0';
    analysisInfoMilestonesAccordionValue: string | undefined;

    // Step 2 - Project info fields
    analysisInfoProjectDetailsAccordionValue: string | undefined = '0';
    projectAreaValue: number | null = null;
    shiftHoursPerDayValue: number | null = null;
    theoreticalBudgetValue: number | null = null;
    theoreticalScheduleWorkdaysValue: number | null = null;
    projectLocationValue: string = '';

    analysisName = '';
    selectedProject: IProject | null = null;
    analysisDueDate: Date | null = null;
    analysisDueDateMax: Date | undefined;
    analysisProjectOptions: SelectOption[] = [];
    analysisProjects: IProject[] = []; // full list for dueDate validation
    analysisMilestones: Partial<IMilestone>[] = [{ name: 'Milestone 1 - Completion of review' }];
    analysisNumberInTotal = 1; // For display name when analysis name is empty: "MMDDYY - {n} - Analysis"

    readonly ANALYSIS_NAME_PLACEHOLDER = 'Analysis name goes here';
    readonly PROJECT_PLACEHOLDER = 'Choose Project';
    readonly DUE_DATE_PLACEHOLDER = 'Select a date';
    readonly DEFAULT_MILESTONE_NAME = 'Completion of review';

    analysisMilestonesPermissions = {
        canView: true,
        canEdit: true,
        canComment: false,
        restricted: false
    };

    // Step 3 - Team share state (for backend teamMembers, workspaceShare, projectShare)
    analysisShareDetails: {
        users: IShareUser[];
        anyone: SHARE_ACCESS;
        workspaceShare: boolean;
        projectShare: boolean;
    } = {
            users: [],
            anyone: SHARE_ACCESS.RESTRICTED,
            workspaceShare: false,
            projectShare: false,
        };

    constructor(
        private router: Router,
        private milestoneService: MileStoneService,
    ) {
        super();
        this.loadAnalysisItems();
        this.initCardMenuItems();
        this.getDocTypeMilestones();
        this.initGenerateExcelAgreementMenuItems();
        this.loadProjectsForAnalysisInfo();
        this.loadUsersForFilter();
        this.setupSocketListeners();
    }

    private setupSocketListeners(): void {
        this.subscribe(
            this.socketService.on<{ geotechnicalAnalysisId: string; status: string }>(
                SOCKET_EVENTS.GEOTECHNICAL_ANALYSIS_CREATION_STATUS_CHANGED
            ),
            (res) => {
                if (res && res[0]) {
                    const { geotechnicalAnalysisId, status } = res[0];
                    this.analysisItems = this.analysisItems.map(item =>
                        item.id === geotechnicalAnalysisId
                            ? { ...item, status }
                            : item
                    );
                }
            }
        );
    }

    private initGenerateExcelAgreementMenuItems(): void {
        this.generateExcelAgreementMenuItems = [
            {
                label: 'See all',
                icon: 'pi pi-list',
                command: () => this.onGenerateExcelSidebarPrimaryAction(),
            },
        ];
    }

    ngOnInit(): void {
        (window as any)['onConnectorConnected'] = () => this.getIntegrationsConnectors();
    }

    getDocTypeMilestones(): void {
        this.milestoneService.getReportMilestones().subscribe(res => {
            if (res.success && res.data?.length) {
                this.analysisMilestones = (res.data || []).map(m => ({
                    ...m,
                    date: m.date ? new Date(m.date) : new Date(new Date().setHours(23, 59, 59, 999)),
                    reminder: m.reminder
                }));
            }
        });
    }

    private createAnalysisStepperConfig(): StepperConfig {
        const steps: StepperStepConfig[] = [
            {
                id: 'lay-the-foundation',
                label: 'Lay the foundation',
                title: 'Lay the foundation',
                subtitle: 'Upload the core information and inputs that drive the analysis',
                svg: GEOTECHNICAL_ANALYSIS_SVG.MILESTONES
            },
            {
                id: 'analysis-information',
                label: 'Analysis information',
                title: 'Analysis information',
                subtitle: 'Establish the analysis identity, project association, and key milestones.',
                svg: GEOTECHNICAL_ANALYSIS_SVG.ANALYSIS_INFORMATION
            },
            {
                id: 'team',
                label: 'Team',
                title: 'Team',
                subtitle: 'Assign team members to this analysis.',
                svg: PROJECT_DASHBOARD_SVG.ADD_TEAM
            }
        ];
        return {
            title: "You're just 3 steps away!",
            subtitle: 'A completely new way to handle your construction documents.',
            steps,
            currentStepIndex: 0,
            dialogWidth: '80vw',
            dialogHeight: '90vh'
        };
    }

    loadAnalysisItems(skip?: number, limit?: number): void {
        const skipNum = skip ?? this.first;
        const limitNum = limit ?? this.rows;
        this.loadingAnalyses.set(true);
        this.tableConfig = { ...this.tableConfig, loading: true };
        const filters: GeotechnicalAnalysisListFilters = {};
        if (this.searchAnalysisName?.trim()) {
            filters.searchValue = this.searchAnalysisName.trim();
        }
        if (this.filterProject?._id) {
            filters.projectId = this.filterProject._id;
        }
        if (this.filterCreatedBy.length > 0) {
            filters.createdBy = this.filterCreatedBy;
        }
        if (this.filterDueDateRange?.length === 2) {
            filters.dueDateStart = this.filterDueDateRange[0].toISOString();
            filters.dueDateEnd = this.filterDueDateRange[1].toISOString();
        }
        if (this.filterDateCreatedRange?.length === 2) {
            filters.dateCreatedStart = this.filterDateCreatedRange[0].toISOString();
            filters.dateCreatedEnd = this.filterDateCreatedRange[1].toISOString();
        }
        this.geotechnicalAnalysisService
            .list({ skip: skipNum, limit: limitNum, filters })
            .subscribe({
                next: (res) => {
                    this.loadingAnalyses.set(false);
                    this.tableConfig = { ...this.tableConfig, loading: false };
                    if (res?.success && res?.data) {
                        this.analysisItems = (res.data.analyses ?? []).map((a) =>
                            this.mapAnalysisToItem(a)
                        );
                        this.totalRecords = res.data.totalRecords ?? 0;
                        this.sortAnalyses();
                    } else {
                        this.analysisItems = [];
                        this.totalRecords = 0;
                    }
                },
                error: () => {
                    this.loadingAnalyses.set(false);
                    this.tableConfig = { ...this.tableConfig, loading: false };
                    this.analysisItems = [];
                    this.totalRecords = 0;
                },
            });
    }

    get templatesMap(): { [key: string]: TemplateRef<unknown> } {
        return {
            reportNameTemplate: this.reportNameTemplateRef,
            projectNameTemplate: this.projectNameTemplateRef,
            creatorNameTemplate: this.creatorNameTemplateRef,
            createdDateTemplate: this.createdDateTemplateRef,
            statusTemplate: this.statusTemplateRef,
            upcomingMilestoneTemplate: this.upcomingMilestoneTemplateRef,
            dueDateTemplate: this.dueDateTemplateRef,
            actionsTemplate: this.actionsTemplateRef,
        };
    }

    sortField: 'reportName' | 'createdAt' | 'dueDate' = 'createdAt';
    sortOrder: 'asc' | 'desc' = 'desc';

    setSortField(field: 'reportName' | 'createdAt' | 'dueDate'): void {
        this.sortField = field;
        this.sortAnalyses();
        this.sortOverlay?.hide();
    }

    setSortOrder(order: 'asc' | 'desc'): void {
        this.sortOrder = order;
        this.sortAnalyses();
        this.sortOverlay?.hide();
    }

    private sortAnalyses(): void {
        this.analysisItems = [...this.analysisItems].sort((a, b) => {
            let aVal: string | number;
            let bVal: string | number;
            if (this.sortField === 'reportName') {
                aVal = (a.reportName ?? '').toLowerCase();
                bVal = (b.reportName ?? '').toLowerCase();
                return this.sortOrder === 'asc'
                    ? (aVal < bVal ? -1 : aVal > bVal ? 1 : 0)
                    : (bVal < aVal ? -1 : bVal > aVal ? 1 : 0);
            }
            aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            if (this.sortField === 'dueDate') {
                aVal = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                bVal = b.dueDate ? new Date(b.dueDate).getTime() : 0;
            }
            return this.sortOrder === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
        });
    }

    onPageChange(event: { first?: number; rows?: number }): void {
        this.first = event.first ?? this.defaultPaginatorValues.first;
        this.rows = event.rows ?? this.defaultPaginatorValues.rows;
        this.loadAnalysisItems(this.first, this.rows);
    }

    onViewModeChange(mode: 'grid' | 'list' | { value: 'grid' | 'list' }): void {
        this.viewMode = typeof mode === 'string' ? mode : (mode?.value ?? 'grid');
    }

    onAnalysisRowClick(item: GeotechnicalAnalysisItem): void {
        // this.onStartAnalysis(item);
        this.router.navigate(['/geotechnical-analysis-report'], { queryParams: { analysisId: item.id } });
    }

    private mapAnalysisToItem(a: GeotechnicalAnalysisListItem): GeotechnicalAnalysisItem {
        const milestoneDate = a.upcomingMilestone?.date
            ? moment(a.upcomingMilestone.date).format('MMM D')
            : '';
        const lastEditedOn = a.lastEditedOn
            ? moment(a.lastEditedOn).format('Do MMM')
            : '—';
        const lastEditedBy =
            a.lastEditedBy?.firstName != null || a.lastEditedBy?.lastName != null
                ? [a.lastEditedBy.firstName, a.lastEditedBy.lastName].filter(Boolean).join(' ').trim()
                : '—';
        const assignedUsers = (a.teamMembers ?? []).map((m) => {
            const u = m.user as { _id?: string; firstName?: string; lastName?: string } | undefined;
            const id = u?._id ?? (m as { _id?: string })._id ?? '';
            const firstName = u?.firstName ?? (m.name ? m.name.split(/\s+/)[0] : '') ?? '';
            const lastName = u?.lastName ?? (m.name ? m.name.split(/\s+/).slice(1).join(' ') : '') ?? '';
            return { id, _id: id, firstName, lastName } as Partial<IUser>;
        });
        return {
            id: a._id,
            reportName: a.displayName ?? a.name ?? '—',
            projectName: (a.project as unknown as IProject)?.name ?? '—',
            creatorName: lastEditedBy,
            createdAt: a.createdAt ?? undefined,
            dueDate: a.dueDate ?? undefined,
            milestoneName: a.upcomingMilestone?.name ?? '—',
            milestoneDate,
            assignedUsers,
            lastEditedOn,
            lastEditedBy,
            status: a.status,
            currentUserRole: this.resolveCurrentUserRole(a),
        };
    }

    private resolveCurrentUserRole(a: GeotechnicalAnalysisListItem): string {
        switch (a.currentUserRole) {
            case SHARE_ACCESS.OWNER: return CONTRACT_USER_ROLE.OWNER;
            case SHARE_ACCESS.EDIT: return CONTRACT_USER_ROLE.EDITOR;
            case SHARE_ACCESS.COMMENT: return CONTRACT_USER_ROLE.COMMENTER;
            default: return CONTRACT_USER_ROLE.VIEWER;
        }
    }

    initCardMenuItems(): void {
        this.cardMenuItems = [
            { label: 'Edit', icon: 'pi pi-pencil' },
            { label: 'Rename', icon: 'pi pi-tag' },
            { label: 'Due date', icon: 'pi pi-calendar' },
            { label: 'Delete', icon: 'pi pi-trash', class: 'text-red-500' }
        ];
    }

    canShowKebabMenu(role: string): boolean {
        return role === CONTRACT_USER_ROLE.OWNER || role === CONTRACT_USER_ROLE.EDITOR;
    }

    get canManageInstructions(): boolean {
        const user = this.authService.getUser();
        if (!user) return false;
        const isAccountOwner = (user as any).type === 'OWNER' || user.workspaceRole === SHARE_ACCESS.OWNER;
        const isOrgAdmin = [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(user.role as USER_ROLE);
        const isWorkspaceAdmin = user.workspaceRole === SHARE_ACCESS.ADMIN;
        return isAccountOwner || isOrgAdmin || isWorkspaceAdmin;
    }

    private updateCardMenuItemsForRole(role: string): void {
        const allItems: CustomMenuItem[] = [
            { label: 'Edit', icon: 'pi pi-pencil' },
            { label: 'Rename', icon: 'pi pi-tag' },
            { label: 'Due date', icon: 'pi pi-calendar' },
            { label: 'Delete', icon: 'pi pi-trash', class: 'text-red-500' },
        ];
        if (role === CONTRACT_USER_ROLE.OWNER) {
            this.cardMenuItems = allItems;
        } else {
            this.cardMenuItems = allItems.filter(i => i.label != 'Delete');
        }
    }

    onGenerateAnalysis(): void {
        this.currentStepIndex.set(0);
        this.stepperService.setCurrentStep(0);
        this.showGenerateAnalysisStepper = true;
        this.setCurrentUserAsOwner();
        this.loadUserPreferences();
    }

    /** Called from Generate analysis excel menu: Generate → open files dialog. */
    onGenerateExcelMenuGenerate(): void {
        this.openFileSelectionFor('generateAnalysisExcel');
    }

    /** Called from Generate analysis excel menu: View → open side panel. */
    onGenerateExcelMenuView(): void {
        this.onGenerateAnalysisExcel();
    }

    /** Set the current user as owner in the team step. */
    private setCurrentUserAsOwner(): void {
        const currentUser = this.authService.getUser();
        if (currentUser) {
            const fullOrFirstLast = currentUser.fullName ?? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ').trim();
            const name = (fullOrFirstLast || currentUser.email) ?? '';
            this.analysisShareDetails.users = [
                {
                    name,
                    user: currentUser,
                    email: currentUser.email ?? '',
                    type: USER_TYPE.INTERNAL,
                    role: SHARE_ACCESS.OWNER,
                } as IShareUser,
            ];
        }
    }

    onAnalysisStepChange(event: { previousIndex: number; currentIndex: number; stepId: string }): void {
        this.currentStepIndex.set(event.currentIndex);
        if (event.currentIndex === 1) {
            this.loadProjectsForAnalysisInfo();
        }
    }

    loadProjectsForAnalysisInfo(): void {
        this.projectService.getProjects(0, 500, {}).subscribe((res) => {
            if (res?.success && res?.data?.projects) {
                this.analysisProjects = res.data.projects;
                this.analysisProjectOptions = this.analysisProjects.map((p) => ({
                    label: p.name || '',
                    value: p
                }));
            }
        });
    }

    toggleFilterPanel(): void {
        this.showFilterPanel = !this.showFilterPanel;
    }

    closeFilterPanel(): void {
        this.showFilterPanel = false;
    }

    loadUsersForFilter(): void {
        this.userService.getUsers().subscribe((res) => {
            const users = res?.data?.users ?? [];
            this.createdByOptions = users.map((u: any) => ({
                label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email,
                value: u._id,
            }));
        });
    }

    getCreatorLabel(id: string): string {
        return this.createdByOptions.find(o => o.value === id)?.label ?? id;
    }

    removeCreatorFilter(id: string): void {
        this.filterCreatedBy = this.filterCreatedBy.filter(x => x !== id);
    }

    clearFilters(): void {
        this.filterProject = null;
        this.filterCreatedBy = [];
        this.filterDueDateRange = null;
        this.filterDateCreatedRange = null;
        this.first = 0;
        this.loadAnalysisItems(0, this.rows);
    }

    applyFilters(): void {
        this.showFilterPanel = false;
        this.first = 0;
        this.loadAnalysisItems(0, this.rows);
    }

    // ── Configurations drawer methods ─────────────────────────────────────────

    /** Opens the configurations drawer and fetches the current workspace config values. */
    openConfigurationsDrawer(): void {
        this.showConfigurationsDrawer = true;
        this.geotechnicalAnalysisService.getConfigurations().subscribe({
            next: (res: any) => {
                const aiVal = res?.data?.[GEOTECHNICAL_CONFIGURATION_TYPE.AI_DASHBOARD]?.value ?? '';
                this.aiDashboardInstructions.set(aiVal);
                const summaryVal = res?.data?.[GEOTECHNICAL_CONFIGURATION_TYPE.SUMMARY_INSTRUCTIONS]?.value ?? '';
                this.summaryInstructions.set(summaryVal);
                const roundVal = res?.data?.[GEOTECHNICAL_CONFIGURATION_TYPE.ROUND_LAYER_THICKNESS]?.value ?? '';
                this.roundLayerThickness.set(roundVal === 'true');
            },
            error: () => {
                this.toastService.error('Failed to load configurations');
            },
        });
        this.loadGeneratedAnalysisExcels();
    }

    /** Closes the configurations drawer and resets any ongoing edit. */
    closeConfigurationsDrawer(): void {
        this.showConfigurationsDrawer = false;
        this.aiDashboardEditing.set(false);
        this.aiDashboardDraft.set('');
        this.summaryEditing.set(false);
        this.summaryDraft.set('');
    }

    /** Enters edit mode for AI Dashboard Instructions, pre-populating the draft. */
    startEditAiDashboardInstructions(): void {
        this.aiDashboardDraft.set(this.aiDashboardInstructions());
        this.aiDashboardEditing.set(true);
    }

    /** Cancels the ongoing edit without saving. */
    cancelEditAiDashboardInstructions(): void {
        this.aiDashboardEditing.set(false);
        this.aiDashboardDraft.set('');
    }

    /** Persists the AI Dashboard Instructions draft to the backend. */
    saveAiDashboardInstructions(): void {
        this.aiDashboardSaving.set(true);
        this.geotechnicalAnalysisService
            .updateConfiguration(GEOTECHNICAL_CONFIGURATION_TYPE.AI_DASHBOARD, this.aiDashboardDraft())
            .subscribe({
                next: (res) => {
                    this.aiDashboardSaving.set(false);
                    this.aiDashboardInstructions.set(res.data?.value ?? this.aiDashboardDraft());
                    if (!res.success) {
                        return;
                    }
                    this.aiDashboardEditing.set(false);
                    this.aiDashboardDraft.set('');
                    this.toastService.success('AI Dashboard Instructions saved');
                },
                error: (err) => {
                    this.aiDashboardSaving.set(false);
                    const message = err.error?.message ?? err.error?.msg ?? 'Failed to save AI Dashboard Instructions';
                    this.toastService.error(message);
                },
            });
    }

    /** Enters edit mode for Summary Instructions, pre-populating the draft. */
    startEditSummaryInstructions(): void {
        this.summaryDraft.set(this.summaryInstructions());
        this.summaryEditing.set(true);
    }

    /** Cancels the ongoing Summary Instructions edit without saving. */
    cancelEditSummaryInstructions(): void {
        this.summaryEditing.set(false);
        this.summaryDraft.set('');
    }

    /** Persists the Summary Instructions draft to the backend. */
    saveSummaryInstructions(): void {
        this.summarySaving.set(true);
        this.geotechnicalAnalysisService
            .updateConfiguration(GEOTECHNICAL_CONFIGURATION_TYPE.SUMMARY_INSTRUCTIONS, this.summaryDraft())
            .subscribe({
                next: (res) => {
                    this.summarySaving.set(false);
                    // Backend may reject (e.g. over character limit) while still persisting a trimmed
                    // value; sync from the returned data either way so the UI never shows stale content.
                    this.summaryInstructions.set(res.data?.value ?? this.summaryDraft());
                    if (!res.success) {
                        return;
                    }
                    this.summaryEditing.set(false);
                    this.summaryDraft.set('');
                    this.toastService.success('Summary Instructions saved');
                },
                error: () => {
                    this.summarySaving.set(false);
                    this.toastService.error('Failed to save Summary Instructions');
                },
            });
    }


    onRoundLayerThicknessToggle(checked: boolean): void {
        const previous = this.roundLayerThickness();
        this.roundLayerThickness.set(checked);
        this.roundLayerThicknessSaving.set(true);
        this.geotechnicalAnalysisService
            .updateConfiguration(GEOTECHNICAL_CONFIGURATION_TYPE.ROUND_LAYER_THICKNESS, checked ? 'true' : 'false')
            .subscribe({
                next: (res) => {
                    this.roundLayerThicknessSaving.set(false);
                    if (!res.success) {
                        this.roundLayerThickness.set(previous);
                        return;
                    }
                    this.toastService.success('Configuration updated');
                },
                error: () => {
                    this.roundLayerThicknessSaving.set(false);
                    this.roundLayerThickness.set(previous);
                    this.toastService.error('Failed to update configuration');
                },
            });
    }

    /** Display name: user input if provided, else "MMDDYY - {analysisNumberInTotal} - Analysis" */
    getDisplayAnalysisName(): string {
        const name = (this.analysisName || '').trim();
        if (name) return name;
        const now = new Date();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const yy = String(now.getFullYear()).slice(-2);
        return `${mm}${dd}${yy} - ${this.analysisNumberInTotal} - Analysis`;
    }

    /** Max date for analysis due date: project due date when project is selected */
    getAnalysisDueDateMaxDate(): Date | undefined {
        const project = this.selectedProject;
        if (!project?.dueDate) return undefined;
        const d = new Date(project.dueDate);
        return d;
    }

    onAnalysisMilestonesChange(updated: Partial<IMilestone>[]): void {
        this.analysisMilestones = updated;
    }

    onAnalysisDueDateChange(date: Date | Date[] | null): void {
        const resolvedDate = Array.isArray(date) ? date[0] ?? null : date;
        this.analysisDueDate = resolvedDate;
        if (resolvedDate) {
            this.analysisMilestones = this.analysisMilestones.map(m => ({
                ...m,
                date: m.date && new Date(m.date) > resolvedDate ? undefined : m.date,
            }));
        }
    }

    getMilestonesMaxDate(): Date | undefined {
        return this.analysisDueDate ?? this.getAnalysisDueDateMaxDate();
    }

    onAnalysisInfoProjectChange(project: IProject | null): void {
        this.selectedProject = project;
        if (!project?.dueDate) {
            this.analysisDueDateMax = undefined;
            return;
        }

        const d = new Date(project.dueDate);
        d.setHours(23, 59, 59, 999);
        this.analysisDueDateMax = d;
    }

    onAnalysisNextStepClick(): void {
        const currentIndex = this.stepperService.currentStepIndex();
        if (currentIndex === 0) {
            // Validate Lay the foundation: all three mandatory
            if (this.reportsFiles.length === 0 || !this.formulaeFile || !this.analysisExcelFile) {
                return; // Optionally show toast; Next can be disabled in template
            }
        }
    }

    private getDefaultFileSelectionConfig(): FileSelectionConfig {
        return {
            title: 'Select file',
            maxTotalUploadedAttachments: 1,
            maxTotalUploadedFileSize: 20 * 100 * 1000 * 1000,
            localUploadMaxFileSize: 100 * 1000 * 1000,
            showTotalAttachmentsCount: false,
            supportSubFolders: true,
            allowedFileTypes: [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.EXCEL],
            tabs: [
                { id: 'workspace', label: 'Workspace knowledge', type: TabType.SELECT },
                { id: 'project', label: 'Project knowledge', type: TabType.SELECT },
                { id: 'documents', label: 'Documents', type: TabType.SELECT },
                { id: 'upload', label: 'Local upload', type: TabType.UPLOAD },
                { id: INTEGRATIONS_TAB_ID, label: 'Integrations', tooltip: 'Select from integrations', type: TabType.SELECT, folderIcon: 'folder', folderIconSource: 'lucide' },
                { id: GENERATED_EXCEL_TAB_ID, label: 'Generated Excel', type: TabType.SELECT },
            ],
            dialogWidth: '60vw',
            dialogHeight: '90vh',
            submitButtonLabel: 'Select',
            cancelButtonLabel: 'Cancel',
        };
    }

    /**
     * Build the list of already-selected files for a given context so they can be
     * pre-checked when the file-selection dialog is reopened.
     * For local uploads, a fresh blob URL is created from the stored File object.
     */
    private getInitialSelectedFiles(context: LayFoundationFileContext): FileItem[] {
        const displays: LayFoundationFileDisplay[] =
            context === 'reports' ? this.reportsFiles
                : context === 'formulae' ? (this.formulaeFile ? [this.formulaeFile] : [])
                    : context === 'analysisExcel' ? (this.analysisExcelFile ? [this.analysisExcelFile] : [])
                        : []; // 'generateAnalysisExcel' always starts fresh

        return displays.map(display => {
            if (display.isLocal && display.file) {
                return {
                    id: display.id,
                    name: display.name,
                    size: display.size,
                    metadata: {
                        isUploaded: true,
                        uploadedFile: display.file,
                        blobUrl: URL.createObjectURL(display.file),
                        tabId: 'upload',
                        dropdownId: 'Uploaded',
                    },
                } as FileItem;
            }
            if (display.fileItem) {
                return display.fileItem; // already carries metadata.tabId / metadata.dropdownId
            }
            return null;
        }).filter((f): f is FileItem => f !== null);
    }

    openFileSelectionFor(context: LayFoundationFileContext): void {
        this.fileSelectionContext = context;
        this.loadKnowledgeBasesForFileSelection();
        this.loadProjectsForFileSelection();
        this.getIntegrationsConnectors();

        const isReports = context === 'reports';
        const isAnalysisExcel = context === 'analysisExcel' || context === 'generateAnalysisExcel';

        const title =
            context === 'reports'
                ? 'Select reports'
                : context === 'formulae'
                    ? 'Select formulas document'
                    : context === 'generateAnalysisExcel'
                        ? 'Generate analysis excel'
                        : 'Select analysis excel';

        let allowedFileTypes: FileDropTypes[] = [];
        if (isAnalysisExcel) {
            if (context === 'generateAnalysisExcel') {
                allowedFileTypes = [FileDropTypes.XLSX, FileDropTypes.PDF, FileDropTypes.DOCX];
            } else {
                allowedFileTypes = [FileDropTypes.XLSX];
            }
        } else {
            allowedFileTypes = [FileDropTypes.PDF, FileDropTypes.DOCX];
        }

        const defaultConfig = this.getDefaultFileSelectionConfig();
        this.fileSelectionConfig.set({
            ...defaultConfig,
            title,
            maxTotalUploadedAttachments: (isReports || context === 'generateAnalysisExcel') ? 20 : (isAnalysisExcel ? 1 : 1),
            showTotalAttachmentsCount: isReports || context === 'generateAnalysisExcel',
            allowedFileTypes: allowedFileTypes as FileDropTypes[],
            initialSelectedFiles: this.getInitialSelectedFiles(context),
            tabs: isAnalysisExcel
                ? defaultConfig.tabs
                : defaultConfig.tabs?.filter(tab => tab.id !== GENERATED_EXCEL_TAB_ID),
        });
        this.openFileSelectionDialog = true;
    }

    loadKnowledgeBasesForFileSelection(): void {
        this.knowledgeBaseService.getKnowledgeBaseStore({ skip: 0, limit: 1000, scope: KNOWLEDGE_SCOPE.WORKSPACE }).subscribe((res) => {
            if (res?.success && res?.data?.stores) {
                this.knowledgeBases = res.data.stores;
                this.prepareFileSelectionDropdownOptions();
            }
        });
    }

    loadProjectsForFileSelection(): void {
        this.projectService.getProjects(0, 1000, {}).subscribe((res) => {
            if (res?.success && res?.data?.projects) {
                this.projects = res.data.projects;
                this.prepareFileSelectionDropdownOptions();
            }
        });
    }

    private prepareFileSelectionDropdownOptions(): void {
        const options: DropdownOption[] = [{ id: 'all', label: 'All files', groupKey: 'metadata.kbId', groupLabel: 'metadata.kbName' }];
        this.knowledgeBases.forEach((kb) => {
            options.push({ id: kb._id, label: kb.name, value: kb, groupKey: 'metadata.kbId', groupLabel: 'metadata.kbName' });
        });
        this.fileSelectionDropdownOptions.set(options);
    }

    private prepareProjectDropdownOptions(): void {
        const options: DropdownOption[] = [{ id: 'all', label: 'All projects', groupKey: 'metadata.projectId', groupLabel: 'metadata.projectName' }];
        this.projects.forEach((p) => {
            options.push({ id: p._id, label: p.name, value: p, groupKey: 'metadata.projectId', groupLabel: 'metadata.projectName' });
        });
        this.fileSelectionDropdownOptions.set(options);
    }

    private prepareDocumentsDropdownOptions(): void {
        const options: DropdownOption[] = [{ id: 'all', label: 'All documents', groupKey: 'metadata.projectId', groupLabel: 'metadata.projectName' }];
        this.projects.forEach((p) => {
            options.push({ id: p._id, label: p.name, value: p, groupKey: 'metadata.projectId', groupLabel: 'metadata.projectName' });
        });
        this.fileSelectionDropdownOptions.set(options);
    }

    getIntegrationsConnectors(): void {
        this.integrationsService.getConnectors().subscribe((res: any) => {
            if (res?.success && res.data) {
                const integrationMapping: { [key: string]: { name: string; icon: string } } = CONNECTOR_PROVIDER_INFO;
                const allowedSet = new Set<string>(INTEGRATIONS_TOOLKIT_PROVIDERS);
                const connectorMenuItems: CustomMenuItem[] = [];
                for (const category of res.data) {
                    if (category?.values && Array.isArray(category.values)) {
                        for (const connector of category.values) {
                            if (!allowedSet.has(connector.name) || !integrationMapping[connector.name]) continue;
                            const mapping = integrationMapping[connector.name];
                            let menuItem: CustomMenuItem = {
                                label: mapping.name,
                                value: connector.name,
                                icon: mapping.icon,
                                iconSource: IconSourceEnum.SVG,
                                subOptions: [],
                                class: '[&>svg]:w-4 [&>svg]:h-4',
                            };
                            if (connector.toolkit === true) {
                                menuItem = { ...menuItem, type: MenuItemType.TOGGLE, toggleState: true };
                            } else if (connector.toolkit === false) {
                                menuItem = { ...menuItem, type: MenuItemType.TOGGLE, toggleState: false };
                            } else if (connector.toolkit === null) {
                                menuItem = { ...menuItem, type: MenuItemType.BUTTON, buttonLabel: 'Connect', buttonClass: 'theme-text border-none' };
                            }
                            connectorMenuItems.push(menuItem);
                        }
                    }
                }
                connectorMenuItems.sort((a, b) => INTEGRATIONS_TOOLKIT_PROVIDERS.indexOf(a.value as any) - INTEGRATIONS_TOOLKIT_PROVIDERS.indexOf(b.value as any));
                this.integrationsConnectorsMenuItems = [...connectorMenuItems, this.manageConnectorsMenuItem];
            }
        });
    }

    prepareIntegrationsDropdownOptions(): void {
        const options: DropdownOption[] = INTEGRATIONS_TOOLKIT_PROVIDERS.map((provider) => {
            const info = CONNECTOR_PROVIDER_INFO[provider];
            return { id: provider, label: info.name, value: provider, icon: info.icon, iconSource: IconSourceEnum.SVG, iconSize: 'lg', iconClass: '[&>svg]:w-4 [&>svg]:h-4' };
        });
        this.fileSelectionDropdownOptions.set(options);
    }

    private getIntegrationConnectorToolkitStatus(connectorId: string | null): 'connected' | 'null' | 'disabled' | null {
        if (!connectorId) return null;
        const item = this.integrationsConnectorsMenuItems.find((m) => m.value === connectorId);
        if (!item) return null;
        if (item.type === MenuItemType.BUTTON) return 'null';
        if (item.type === MenuItemType.TOGGLE) return item.toggleState === true ? 'connected' : 'disabled';
        return null;
    }

    getIntegrationConnectorConnected(connectorId: string | null): boolean {
        if (!connectorId) return false;
        const item = this.integrationsConnectorsMenuItems.find((m) => m.value === connectorId);
        return item?.type === MenuItemType.TOGGLE && item?.toggleState === true;
    }

    onIntegrationsConnectorSelect(option: CustomMenuItem, menu?: MenuComponent): void {
        if (option.value === 'manage-connectors') {
            this.router.navigate(['/settings/integrations']);
            return;
        }
        const toolKitValue = option.value as TOOLKIT_PROVIDERS;
        if (option.type === MenuItemType.BUTTON) {
            menu?.hide();
            option.buttonLoading = true;
            option.disabled = true;
            option.buttonLabel = '';
            this.integrationsService.addConnector({ toolKit: toolKitValue, callbackUrl: 'explore/callback' }).subscribe((res: any) => {
                if (res?.data?.status) {
                    this.toastService.success('Connection initiated successfully');
                    if (res.data.redirectUrl) {
                        window.open(res.data.redirectUrl, '_blank');
                    }
                } else {
                    this.toastService.error('Connection failed', 'Connection failed');
                    option.buttonLoading = false;
                    option.disabled = false;
                    option.buttonLabel = 'Connect';
                }
                this.getIntegrationsConnectors();
            });
            return;
        }
        if (option.toggleState) {
            this.integrationsService.enableConnector(toolKitValue).subscribe((res: any) => {
                if (res?.data) {
                    this.toastService.success('Connection enabled successfully');
                } else {
                    this.toastService.error('Connection enable failed');
                }
                this.getIntegrationsConnectors();
            });
        } else {
            this.integrationsService.disableConnector(toolKitValue).subscribe((res: any) => {
                if (res?.data) {
                    this.toastService.success('Connection disabled successfully');
                } else {
                    this.toastService.error('Connection disable failed');
                }
                this.getIntegrationsConnectors();
            });
        }
    }

    private normalizeIntegrationListResponse(
        connector: string,
        data: any,
        pageSize: number,
        siteUrl?: string
    ): { items: FileItem[]; nextPageToken?: string; nextCursor?: string; hasMore?: boolean } {
        const items: FileItem[] = [];
        let rawList: any[] = [];
        if (Array.isArray(data)) rawList = data;
        else if (data?.files && Array.isArray(data.files)) rawList = data.files;
        else if (data?.entries && Array.isArray(data.entries)) rawList = data.entries;
        else if (data?.value && Array.isArray(data.value)) rawList = data.value;
        else if (data?.children && Array.isArray(data.children)) rawList = data.children;
        for (const entry of rawList) {
            const isFolder = entry.mimeType === 'application/vnd.google-apps.folder' || entry.folder != null || entry['.tag'] === 'folder' || entry.file?.isFolder || entry.type === 'folder';
            const name = entry.name ?? entry.path_display?.split('/').pop() ?? entry.path_display ?? entry.file?.name ?? 'Unknown';
            let id: string;
            let metadata: Record<string, unknown> = { connector };
            if (connector === TOOLKIT_PROVIDERS.GOOGLE_DRIVE) {
                id = entry.id ?? '';
                metadata = { ...metadata, fileId: entry.id, mime_type: entry.mimeType };
            } else if (connector === TOOLKIT_PROVIDERS.DROPBOX) {
                id = entry.path_lower ?? entry.path_display ?? entry.id ?? '';
                metadata = { ...metadata, path: entry.path_lower ?? entry.path_display };
            } else if (connector === TOOLKIT_PROVIDERS.ONE_DRIVE) {
                id = this.buildOneDrivePath(entry);
                metadata = { ...metadata, fileId: entry.id, file_name: entry.name };
            } else if (connector === TOOLKIT_PROVIDERS.SHAREPOINT) {
                const relativeUrl = entry.serverRelativeUrl ?? entry.id ?? entry.path ?? '';
                id = isFolder && siteUrl ? `${siteUrl}|||${relativeUrl}` : relativeUrl;
                const baseUrl = siteUrl ? siteUrl.replace(/\/+$/, '') : undefined;
                metadata = { ...metadata, serverRelativeUrl: relativeUrl, path: entry.path ?? relativeUrl, ...(baseUrl ? { siteUrl: baseUrl } : {}) };
            } else if (connector === TOOLKIT_PROVIDERS.OUTLOOK) {
                id = entry.id ?? '';
                metadata = { ...metadata, messageId: entry.id, subject: entry.subject };
            } else {
                id = entry.id ?? entry.path ?? entry.serverRelativeUrl ?? '';
                metadata = { ...metadata, fileId: entry.id, path: entry.path, serverRelativeUrl: entry.serverRelativeUrl };
            }
            items.push({ id, name, size: entry.size ?? entry.file?.size, isFolder: !!isFolder, metadata: { ...metadata, connector } });
        }
        const nextPageToken = data?.nextPageToken ?? (data as any)?.['@odata.nextLink'];
        const nextCursor = data?.cursor;
        const hasMore = !!nextPageToken || !!nextCursor || (data?.has_more === true);
        return { items, nextPageToken, nextCursor, hasMore };
    }

    buildOneDrivePath(entry: any): string {
        const parentPath = entry.parentReference?.path || '/drive/root:';
        const cleanParent = parentPath.replace('/drive/root:', '');
        if (!cleanParent) return `/${entry.name}`;
        return `${cleanParent}/${entry.name}`;
    }

    private normalizeSharepointSites(data: any): { items: FileItem[]; nextPageToken?: string; nextCursor?: string; hasMore?: boolean } {
        const rawList: any[] = Array.isArray(data) ? data : [];
        const items: FileItem[] = rawList.map((site: any) => ({
            id: `${site.WebId}|||${site.Path ?? site.SiteName ?? ''}`,
            name: site.Title ?? 'Unknown Site',
            isFolder: true,
            metadata: { connector: TOOLKIT_PROVIDERS.SHAREPOINT, siteUrl: site.Path ?? site.SiteName, siteId: site.SiteId, webId: site.WebId, isSite: true },
        }));
        return { items, hasMore: false };
    }

    fetchIntegrationsFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        const connector = event.dropdownId ?? '';
        const status = this.getIntegrationConnectorToolkitStatus(connector);
        if (status === 'null') {
            this.toastService.warn('Connect your account from the Connectors menu to view files.');
            return of({ items: [], totalRecords: 0 });
        }
        if (status === 'disabled') {
            this.toastService.warn('Enable this connector from the Connectors menu to view files.');
            return of({ items: [], totalRecords: 0 });
        }
        if (status !== 'connected') {
            return of({ items: [], totalRecords: 0 });
        }
        if (connector === TOOLKIT_PROVIDERS.OUTLOOK) {
            return this.fetchOutlookMails(event);
        }
        const pageSize = event.pageSize || 20;
        const key = `${event.tabId}:${connector}:${event.parentFolderId ?? 'root'}`;
        const saved = this.integrationPaginationState[key];
        const params: any = { connector, pageSize, page: event.page === 0 ? 1 : event.page + 1 };
        let fullSiteUrl: string | undefined;
        if (event.parentFolderId) {
            if (connector === TOOLKIT_PROVIDERS.GOOGLE_DRIVE) {
                params.folderId = event.parentFolderId;
            } else if (connector === TOOLKIT_PROVIDERS.ONE_DRIVE) {
                params.folderPath = event.parentFolderId;
            } if (connector === TOOLKIT_PROVIDERS.DROPBOX) {
                params.folderPath = event.parentFolderId;
            } else if (connector === TOOLKIT_PROVIDERS.SHAREPOINT) {
                if (event.parentFolderId.includes('|||')) {
                    const [firstPart, secondPart] = event.parentFolderId.split('|||', 2);
                    if (firstPart.startsWith('http')) {
                        fullSiteUrl = firstPart;
                        params.siteUrl = firstPart;
                        params.serverRelativeUrl = secondPart;
                    } else {
                        fullSiteUrl = secondPart;
                        params.siteUrl = secondPart;
                    }
                } else {
                    params.serverRelativeUrl = event.parentFolderId;
                }
            }
        }
        if (event.page > 0 && saved) {
            if (saved.nextPageToken) params.pageToken = saved.nextPageToken;
            if (saved.nextCursor) params.cursor = saved.nextCursor;
        }
        return this.integrationsService.listFiles(params).pipe(
            map((res: any) => {
                const payload = res?.data?.data ?? res;
                const dataKind = res?.data?.dataKind;
                const normalized =
                    connector === TOOLKIT_PROVIDERS.SHAREPOINT && dataKind === 'sites'
                        ? this.normalizeSharepointSites(payload)
                        : this.normalizeIntegrationListResponse(connector, payload, pageSize, fullSiteUrl);
                const nextPageToken = res?.data?.hasMore ? res?.data?.nextPageToken : normalized.nextPageToken;
                const nextCursor = res?.data?.nextCursor ?? normalized.nextCursor;
                if (nextPageToken || nextCursor) {
                    this.integrationPaginationState[key] = { nextPageToken, nextCursor };
                }
                const totalRecords = normalized.items.length + (res?.data?.hasMore ? pageSize : 0);
                return { items: normalized.items, totalRecords };
            }),
            catchError(() => of({ items: [], totalRecords: 0 }))
        );
    }

    private fetchOutlookMails(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        const connector = TOOLKIT_PROVIDERS.OUTLOOK;

        if (event.parentFolderId) {
            const mail = this.outlookMailsCache.get(event.parentFolderId);
            if (mail) {
                const items: FileItem[] = (mail.attachments ?? []).map((a) => ({
                    id: a.id,
                    name: a.name ?? 'Attachment',
                    size: a.size,
                    isFolder: false,
                    metadata: { connector, messageId: mail.id, attachmentId: a.id, subject: mail.subject, contentType: a.contentType },
                }));
                return of({ items, totalRecords: items.length });
            }
            return of({ items: [], totalRecords: 0 });
        }

        const pageSize = event.pageSize || 20;
        const key = `${event.tabId}:${connector}:root`;
        const saved = this.integrationPaginationState[key];
        const params: any = { connector, pageSize };
        if (event.page > 0 && saved?.nextPageToken) {
            params.pageToken = saved.nextPageToken;
        }

        return this.integrationsService.listMails(params).pipe(
            map((res: any) => {
                const mails: MailMessageInfo[] = res?.data?.data ?? [];
                mails.forEach((mail) => this.outlookMailsCache.set(mail.id, mail));
                const items: FileItem[] = mails
                    .filter((mail) => mail.hasAttachments)
                    .map((mail) => ({
                        id: mail.id,
                        name: mail.subject ?? 'No Subject',
                        isFolder: true,
                        folderIcon: 'mail',
                        folderIconSource: 'lucide',
                        metadata: { connector, messageId: mail.id, subject: mail.subject },
                    }));
                const nextPageToken = res?.data?.nextPageToken;
                if (nextPageToken) {
                    this.integrationPaginationState[key] = { nextPageToken };
                }
                const hasMore = res?.data?.hasMore;
                return { items, totalRecords: items.length + (hasMore ? pageSize : 0) };
            }),
            catchError(() => of({ items: [], totalRecords: 0 }))
        );
    }

    fileSelectionFetch: FetchFunction = (event: FileSelectionChangeEvent) => {
        if (event.tabId === 'workspace') {
            if (event.dropdownId === 'all') return this.fetchAllKnowledgeBaseFiles(event);
            if (event.dropdownId) return this.fetchKnowledgeBaseFiles(event.dropdownId, event);
            this.prepareFileSelectionDropdownOptions();
            return of({ items: [], totalRecords: 0 });
        }
        if (event.tabId === 'project') {
            if (event.dropdownId === 'all') return this.fetchAllProjectKnowledgeFiles(event);
            if (event.dropdownId) return this.fetchProjectKnowledgeFiles(event.dropdownId, event);
            this.prepareProjectDropdownOptions();
            return of({ items: [], totalRecords: 0 });
        }
        if (event.tabId === 'documents') {
            if (event.dropdownId === 'all') return this.fetchAllProjectDocuments(event);
            if (event.dropdownId) return this.fetchProjectDocuments(event.dropdownId, event);
            this.prepareDocumentsDropdownOptions();
            return of({ items: [], totalRecords: 0 });
        }
        if (event.tabId === INTEGRATIONS_TAB_ID) {
            this.prepareIntegrationsDropdownOptions();
            if (event.dropdownId && INTEGRATIONS_TOOLKIT_PROVIDERS.includes(event.dropdownId as (typeof INTEGRATIONS_TOOLKIT_PROVIDERS)[number])) {
                return this.fetchIntegrationsFiles(event);
            }
            return of({ items: [], totalRecords: 0 });
        }
        if (event.tabId === GENERATED_EXCEL_TAB_ID) {
            this.fileSelectionDropdownOptions.set([]);
            return this.fetchGeneratedExcelFiles(event);
        }
        return of({ items: [], totalRecords: 0 });
    };

    private fetchGeneratedExcelFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        return this.geotechnicalAnalysisService.listGeneratedAnalysisExcels({ skip: event.page * event.pageSize, limit: event.pageSize }).pipe(map((res) => {
            const data = res?.success && res?.data
                ? (res.data as { items?: GeneratedAnalysisExcelListItem[]; total?: number })
                : null;
            const rawItems = data?.items ?? [];
            const filtered = event.searchQuery
                ? rawItems.filter(i => (i.name ?? '').toLowerCase().includes((event.searchQuery ?? '').toLowerCase()))
                : rawItems;
            const items: FileItem[] = filtered.map(item => ({
                id: item._id ?? '',
                name: item.name ?? 'Unnamed',
                metadata: {
                    generatedExcelId: true,
                    connector: GENERATED_EXCEL_TAB_ID,
                    fileType: FILE_TYPE.EXCEL,
                },
            }));
            return { items, totalRecords: data?.total ?? items.length };
        }),
            catchError(() => of({ items: [], totalRecords: 0 }))
        );
    }

    private fetchAllKnowledgeBaseFiles(event: FileSelectionChangeEvent) {
        return this.repositoryService.getFiles().pipe(
            map((res) => {
                const allFiles: FileItem[] = [];
                const files = (res?.data || []).filter((f: { type: string }) => f.type === 'FILE');
                const filtered = event.searchQuery
                    ? files.filter((f: { name?: string }) => (f.name || '').toLowerCase().includes((event.searchQuery || '').toLowerCase()))
                    : files;
                filtered.forEach((file: any) => {
                    const firstKb = file.knowledgeBases?.[0];
                    const kbDetails = firstKb ? this.knowledgeBases.find((k) => k._id === firstKb._id) : undefined;
                    allFiles.push({
                        id: file._id,
                        name: file.name || 'Unknown',
                        size: file.size,
                        metadata: { kbId: kbDetails?._id ?? 'file-repository', kbName: kbDetails?.name ?? 'Files', originalFile: file },
                    });
                });
                const start = event.page * event.pageSize;
                const page = allFiles.slice(start, start + event.pageSize);
                return { items: page, totalRecords: allFiles.length };
            })
        );
    }

    private fetchKnowledgeBaseFiles(kbId: string, event: FileSelectionChangeEvent) {
        const kb = this.knowledgeBases.find((k) => k._id === kbId);
        if (!kb?.files) return of({ items: [], totalRecords: 0 });
        let items: FileItem[] = kb.files
            .filter((f: any) => !event.searchQuery || (f.file?.name || '').toLowerCase().includes((event.searchQuery || '').toLowerCase()))
            .map((f: any) => ({
                id: f.file?._id,
                name: f.file?.name || 'Unknown',
                size: f.file?.size,
                metadata: { kbId: kb._id, kbName: kb.name, originalFile: f.file },
            }));
        const start = event.page * event.pageSize;
        return of({ items: items.slice(start, start + event.pageSize), totalRecords: items.length });
    }

    private fetchAllProjectDocuments(event: FileSelectionChangeEvent) {
        const skip = event.page * event.pageSize;
        return this.contractService.getContracts({
            type: CONTRACT_TYPE.ALL,
            skip,
            limit: event.pageSize,
            name: event.searchQuery || '',
        }).pipe(
            map((res) => {
                const list: FileItem[] = (res?.data?.contracts || []).map((doc: any) => {
                    const project = this.projects.find((p) => p._id === doc.projectId);
                    return {
                        id: doc.attachment,
                        name: doc.name || 'Unknown',
                        size: doc?.fileSize || 0,
                        metadata: { projectId: doc.projectId, projectName: project?.name || doc.project?.name, originalDoc: doc },
                    };
                });
                return { items: list, totalRecords: res?.data?.total ?? 0 };
            })
        );
    }

    private fetchProjectDocuments(projectId: string, event: FileSelectionChangeEvent) {
        return this.contractService.getContracts({
            type: CONTRACT_TYPE.ALL,
            skip: event.page * event.pageSize,
            limit: event.pageSize,
            name: event.searchQuery || '',
            projectId,
        }).pipe(
            map((res) => {
                const project = this.projects.find((p) => p._id === projectId);
                const list: FileItem[] = (res?.data?.contracts || []).map((doc: any) => ({
                    id: doc.attachment,
                    name: doc.name || 'Unknown',
                    metadata: { projectId: project?._id, projectName: project?.name, originalDoc: doc },
                }));
                return { items: list, totalRecords: res?.data?.total ?? 0 };
            })
        );
    }

    private fetchAllProjectKnowledgeFiles(event: FileSelectionChangeEvent) {
        this.prepareProjectDropdownOptions();

        if (event.parentFolderId) {
            return this.repositoryService.getFolderDetails(event.parentFolderId).pipe(
                map((res: any) => {
                    const childs = res?.data?.childs ?? res?.childs ?? [];
                    const filtered = childs.filter((f: any) => !event.searchQuery || f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase()));
                    const allItems: FileItem[] = filtered.map((file: any) => {
                        if (file.type === 'FOLDER') return { id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} };
                        const project = this.projects.find((p) => p._id === (file.project?._id ?? file.project));
                        return { id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { projectId: project?._id ?? 'no-project', projectName: project?.name ?? 'No project', originalFile: file, isProjectKnowledge: true } };
                    });
                    const start = event.page * event.pageSize;
                    return { items: allItems.slice(start, start + event.pageSize), totalRecords: allItems.length };
                }),
                catchError(() => of({ items: [], totalRecords: 0 }))
            );
        }

        return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT }).pipe(
            map((res) => {
                const raw = (res as any)?.data ?? [];
                const filtered = raw.filter((f: any) => !event.searchQuery || f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase()));
                const allFiles: FileItem[] = filtered.map((file: any) => {
                    if (file.type === 'FOLDER') return { id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} };
                    const project = this.projects.find((p) => p._id === (file.project?._id ?? file.project));
                    return { id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { projectId: project?._id ?? 'no-project', projectName: project?.name ?? 'No project', originalFile: file, isProjectKnowledge: true } };
                });
                const start = event.page * event.pageSize;
                return { items: allFiles.slice(start, start + event.pageSize), totalRecords: allFiles.length };
            })
        );
    }

    private fetchProjectKnowledgeFiles(projectId: string, event: FileSelectionChangeEvent) {
        this.prepareProjectDropdownOptions();

        const project = this.projects.find((p) => p._id === projectId);
        if (!project) return of({ items: [], totalRecords: 0 });

        const allKnowledgeFolderId = ALL_KNOWLEDGE_FOLDER_PREFIX + projectId;

        // Navigated into "All Knowledge" virtual folder — show all project files flat
        if (event.parentFolderId === allKnowledgeFolderId) {
            return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId }).pipe(
                map((res: any) => {
                    const raw = res?.data ?? [];
                    const items: FileItem[] = raw
                        .filter((f: any) => f.type === 'FILE' && (!event.searchQuery || f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase())))
                        .map((file: any) => ({ id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { projectId: project._id, projectName: project.name, originalFile: file, isProjectKnowledge: true } }));
                    const start = event.page * event.pageSize;
                    return { items: items.slice(start, start + event.pageSize), totalRecords: items.length };
                }),
                catchError(() => of({ items: [], totalRecords: 0 }))
            );
        }

        // Navigated into a specific KB store folder — show its files
        if (event.parentFolderId) {
            return this.knowledgeBaseService.getKnowledgeBaseStoreById(event.parentFolderId).pipe(
                map((res: any) => {
                    const store = res?.data ?? res;
                    if (!store?.files) return { items: [], totalRecords: 0 };
                    const breadcrumbLabel = `${project.name} > ${store.name || 'Unknown'}`;
                    let list: FileItem[] = store.files.map((item: any) => ({ id: item.file?._id, name: item.file?.name || 'Unknown', size: item.file?.size, metadata: { projectId: project._id, projectName: project.name, kbId: store._id, kbName: store.name, breadcrumbLabel, originalFile: item.file, isProjectKnowledge: true } }));
                    if (event.searchQuery) list = list.filter((f) => f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase()));
                    const start = event.page * event.pageSize;
                    return { items: list.slice(start, start + event.pageSize), totalRecords: list.length };
                }),
                catchError(() => of({ items: [], totalRecords: 0 }))
            );
        }

        // Root level — show KB stores as folders + "All Knowledge" virtual folder
        return this.knowledgeBaseService.getKnowledgeBaseStore({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId, skip: event.page === 0 ? 0 : event.page * event.pageSize - 1, limit: event.page === 0 ? event.pageSize - 1 : event.pageSize, search: event.searchQuery || '' }).pipe(
            map((res) => {
                const stores = res?.data?.stores ?? [];
                const storeItems: FileItem[] = stores.map((s: any) => ({ id: s._id, name: s.name || 'Unknown', isFolder: true, metadata: { kbId: s._id, kbName: s.name } }));
                const allKnowledgeFolder: FileItem = { id: allKnowledgeFolderId, name: 'All Knowledge', isFolder: true, metadata: {} };
                const items: FileItem[] = event.page === 0 ? [allKnowledgeFolder, ...storeItems] : storeItems;
                return { items, totalRecords: (res?.data?.total ?? 0) + 1 };
            }),
            catchError(() => of({ items: [], totalRecords: 0 }))
        );
    }

    private fetchDocuments(event: FileSelectionChangeEvent) {
        return this.contractService.getDocumentsByType('', {
            search: event.searchQuery || '',
            skip: event.page * event.pageSize,
            limit: event.pageSize,
            excludeDocs: [],
        }).pipe(
            map((res) => {
                const contracts = (res?.data?.contracts ?? []) as IContract[];
                const items: FileItem[] = contracts.map((c) => ({
                    id: c._id,
                    name: c.name || 'Unknown',
                    metadata: { originalContract: c },
                }));
                return { items, totalRecords: res?.data?.total ?? 0 };
            })
        );
    }

    onFileSelectionSubmit(event: FileSelectionSubmitEvent): void {
        const ctx = this.fileSelectionContext;
        if (!ctx) return;

        const displayList = (files: FileItem[], uploaded: File[] | undefined): LayFoundationFileDisplay[] => {
            const out: LayFoundationFileDisplay[] = [];
            (files || []).forEach((f) => {
                const source = f.metadata?.generatedExcelId
                    ? 'Generated Excel'
                    : f.metadata?.kbName
                        ? 'Knowledge base'
                        : f.metadata?.projectName
                            ? 'Project file'
                            : f.metadata?.connector
                                ? (CONNECTOR_PROVIDER_INFO[f.metadata.connector as keyof typeof CONNECTOR_PROVIDER_INFO]?.name ?? 'Integration')
                                : 'Documents';
                out.push({
                    id: f.id,
                    name: f.name || 'Unknown',
                    size: f.size,
                    sourceLabel: source,
                    isLocal: false,
                    fileItem: f,
                });
            });
            (uploaded || []).forEach((file) => {
                out.push({
                    id: `local-${file.name}-${file.size}`,
                    name: file.name,
                    size: file.size,
                    sourceLabel: 'Local file',
                    isLocal: true,
                    file,
                });
            });
            return out;
        };

        const allSelected = event.allSelectedFiles || [];
        const uploaded = event.uploadedFiles || [];
        if (ctx === 'reports') {
            const all = displayList(allSelected, uploaded);
            this.reportsFiles = all.filter((f, i, arr) => arr.findIndex(x => x.id === f.id) === i);
        } else if (ctx === 'formulae') {
            const combined = displayList(allSelected, uploaded);
            this.formulaeFile = combined.length > 0 ? combined[0] : null;
            this.setFormulaeAsDefault = false;
        } else if (ctx === 'analysisExcel') {
            const combined = displayList(allSelected, uploaded);
            this.analysisExcelFile = combined.length > 0 ? combined[0] : null;
            this.setAnalysisExcelAsDefault = false;
            this.analysisExcelIsFromDefault = false;
        } else if (ctx === 'generateAnalysisExcel') {
            const combined = displayList(allSelected, uploaded);
            if (combined.length === 0) {
                this.toastService.warn('Please select at least one file');
                return;
            }
            this.openFileSelectionDialog = false;
            this.fileSelectionContext = null;
            this.submitGenerateAnalysisExcel(combined);
            return;
        }
        this.openFileSelectionDialog = false;
        this.fileSelectionContext = null;
    }

    /**
     * Call separate backend endpoint for "Generate analysis excel" with only analysis excel files.
     * FormData: optional data (JSON with analysisExcels refs only) + analysisExcelFile(s) for local uploads.
     */
    private submitGenerateAnalysisExcel(displays: LayFoundationFileDisplay[]): void {
        if (this.savingAnalysis()) return;
        const refs = displays.filter((d) => !d.isLocal).map((d) => this.getInputRef(d));
        const formData = new FormData();
        if (refs.length > 0) {
            formData.append('data', JSON.stringify({ analysisExcels: refs }));
        }
        displays.forEach((d) => {
            const localFile = this.getLocalFile(d);
            if (localFile) {
                formData.append('analysisExcelFile', localFile);
            }
        });
        this.savingAnalysis.set(true);
        this.geotechnicalAnalysisService.generateAnalysisExcel(formData).subscribe({
            next: (res) => {
                this.savingAnalysis.set(false);
                if (res?.success && res?.data) {
                    this.toastService.success('Analysis generation is in progress');
                    this.loadAnalysisItems();
                    if (this.showConfigurationsDrawer) {
                        this.loadGeneratedAnalysisExcels();
                    }
                } else {
                    this.toastService.error(res?.message ?? 'Failed to create analysis');
                }
            },
            error: (err) => {
                this.savingAnalysis.set(false);
                const msg = err?.error?.message ?? err?.message ?? 'An error occurred while creating analysis';
                this.toastService.error(msg);
            },
        });
    }

    onFileSelectionCancel(): void {
        this.openFileSelectionDialog = false;
        this.fileSelectionContext = null;
    }

    getFileIconType(name: string): FILE_TYPE {
        const ext = (name || '').split('.').pop()?.toLowerCase();
        if (ext === 'pdf') return FILE_TYPE.PDF;
        if (['xlsx', 'xls'].includes(ext || '')) return FILE_TYPE.EXCEL;
        if (['docx', 'doc'].includes(ext || '')) return FILE_TYPE.WORD;
        return FILE_TYPE.PDF;
    }

    formatFileSize(bytes?: number): string {
        if (bytes == null) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }

    removeReportsFile(index: number): void {
        this.reportsFiles = this.reportsFiles.filter((_, i) => i !== index);
    }

    readonly geoAttachmentChips: AttachmentFilterChip[] = [
        { label: 'All', docModel: 'ALL' },
        { label: 'Workspace Knowledge', docModel: 'WORKSPACE_KNOWLEDGE' },
        { label: 'Project Knowledge', docModel: 'PROJECT_KNOWLEDGE' },
        { label: 'Documents', docModel: 'DOCUMENTS' },
        { label: 'Local upload', docModel: 'FILES_FROM_LOCAL' },
    ];

    private sourceLabelToCategory(sourceLabel: string): string {
        switch (sourceLabel) {
            case 'Knowledge base': return 'WORKSPACE_KNOWLEDGE';
            case 'Project file': return 'PROJECT_KNOWLEDGE';
            case 'Documents': return 'DOCUMENTS';
            case 'Generated Excel': return 'GENERATED_EXCELS';
            default: return 'FILES_FROM_LOCAL';
        }
    }

    get reportsFilesAsChatAttachments(): ChatAttachment[] {
        return this.reportsFiles.map(f => ({
            _id: f.id,
            name: f.name,
            docModel: DOCUMENT_MODEL.ATTACHMENT,
            type: FILE_TYPE.PDF,
            category: this.sourceLabelToCategory(f.sourceLabel),
        }));
    }

    onRemoveReportsAttachment(attachment: ChatAttachment): void {
        this.reportsFiles = this.reportsFiles.filter(f => f.id !== attachment._id);
    }

    get formulaeFileAsChatAttachments(): ChatAttachment[] {
        if (!this.formulaeFile) return [];
        return [{
            _id: this.formulaeFile.id,
            name: this.formulaeFile.name,
            docModel: DOCUMENT_MODEL.ATTACHMENT,
            type: FILE_TYPE.PDF,
            category: this.sourceLabelToCategory(this.formulaeFile.sourceLabel),
        }];
    }

    onRemoveFormulaeAttachment(): void {
        this.formulaeFile = null;
        this.setFormulaeAsDefault = false;
    }

    get analysisExcelFileAsChatAttachments(): ChatAttachment[] {
        if (!this.analysisExcelFile) return [];
        return [{
            _id: this.analysisExcelFile.id,
            name: this.analysisExcelFile.name,
            docModel: DOCUMENT_MODEL.ATTACHMENT,
            type: FILE_TYPE.PDF,
            category: this.sourceLabelToCategory(this.analysisExcelFile.sourceLabel),
        }];
    }

    onRemoveAnalysisExcelAttachment(): void {
        this.analysisExcelFile = null;
        this.setAnalysisExcelAsDefault = false;
        this.analysisExcelIsFromDefault = false;
    }

    clearFormulaeFile(): void {
        this.formulaeFile = null;
    }

    clearAnalysisExcelFile(): void {
        this.analysisExcelFile = null;
    }

    onDownloadStandardAnalysisExcel(): void {
        const hasReportsAndFormulaeSelected =
            !this.isEditMode && (this.reportsFiles.length > 0 || this.formulaeFile !== null);
        if (hasReportsAndFormulaeSelected) {
            this.pendingUnsavedChangesAction = () => {
                this.onStepperClose();
                this.openGenerateExcelSidebar();
            };
            this.showUnsavedChangesDialog = true;
        } else {
            this.onStepperClose();
            this.openGenerateExcelSidebar();
        }
    }

    /**
     * Map UI file display to backend source: 'local' | 'knowledgeBase' | 'project' | 'documents' | 'generatedAnalysisExcel'
     */
    private getInputRef(display: LayFoundationFileDisplay): GeotechnicalInputRef {
        if (display.isLocal) return { source: 'local' };
        const meta = display.fileItem?.metadata as {
            kbId?: string; isProjectKnowledge?: boolean; generatedExcelId?: boolean;
            connector?: string; messageId?: string; attachmentId?: string;
            fileId?: string; path?: string; serverRelativeUrl?: string; siteUrl?: string;
        } | undefined;
        if (meta?.kbId) return { source: 'knowledgeBase', file: display.fileItem?.id };
        if (meta?.isProjectKnowledge) return { source: 'project', file: display.fileItem?.id };
        if (meta?.generatedExcelId) return { source: 'generatedAnalysisExcel', attachment: display.fileItem?.id };
        if (meta?.connector === TOOLKIT_PROVIDERS.OUTLOOK) {
            return { source: 'integration', connector: meta.connector, messageId: meta.messageId, attachmentId: meta.attachmentId };
        }
        if (meta?.connector) {
            return { source: 'integration', connector: meta.connector, fileId: meta.fileId, path: meta.path, serverRelativeUrl: meta.serverRelativeUrl, siteUrl: meta.siteUrl };
        }
        return { source: 'documents', attachment: display.fileItem?.id };
    }

    /**
     * Load the "use for future analysis" excel file and pre-select it in the analysis excel slot if one exists.
     */
    private loadUserPreferences(): void {
        this.geotechnicalAnalysisService.getUserPreferences().subscribe({
            next: (res) => {
                if (!res?.success || !res?.data) return;
                const prefs = res.data as UserGeotechnicalPreference;

                if (prefs.defaultFormulae) {
                    this.formulaeFile = {
                        id: prefs.defaultFormulae.attachmentId,
                        name: prefs.defaultFormulae.name,
                        sourceLabel: 'Default',
                        isLocal: false,
                        fileItem: {
                            id: prefs.defaultFormulae.attachmentId,
                            name: prefs.defaultFormulae.name,
                            metadata: { attachmentId: prefs.defaultFormulae.attachmentId, inputRef: prefs.defaultFormulae.ref },
                        } as FileItem,
                    };
                    this.setFormulaeAsDefault = true;
                    this.formulaePrePopulatedFromDefault = true;
                }

                if (prefs.defaultAnalysisExcel) {
                    this.defaultAnalysisExcelAttachmentId = prefs.defaultAnalysisExcel.attachmentId;
                    this.analysisExcelFile = {
                        id: prefs.defaultAnalysisExcel.attachmentId,
                        name: prefs.defaultAnalysisExcel.name,
                        sourceLabel: 'Default',
                        isLocal: false,
                        fileItem: {
                            id: prefs.defaultAnalysisExcel.attachmentId,
                            name: prefs.defaultAnalysisExcel.name,
                            metadata: { attachmentId: prefs.defaultAnalysisExcel.attachmentId, inputRef: prefs.defaultAnalysisExcel.ref },
                        } as FileItem,
                    };
                    this.setAnalysisExcelAsDefault = true;
                    this.analysisExcelIsFromDefault = true;
                    this.analysisExcelPrePopulatedFromDefault = true;
                }
            },
            error: () => { /* ignore; no pre-selection if fetch fails */ },
        });
    }

    /**
     * Build the JSON payload for body.data. Only include refs for non-local (file) sources.
     * Local uploads are sent via FormData (reportFiles, formulaFile, analysisExcelFile), not in data.
     */
    private buildSavePayload(): GeotechnicalSaveDataPayload {
        const reports: GeotechnicalInputRef[] = this.reportsFiles
            .filter((f) => !f.isLocal)
            .map((f) => this.getInputRef(f));
        const formulae =
            this.formulaeFile && !this.formulaeFile.isLocal
                ? this.getInputRef(this.formulaeFile)
                : null;
        const analysisExcel =
            this.analysisExcelFile && !this.analysisExcelFile.isLocal
                ? this.getInputRef(this.analysisExcelFile)
                : null;

        return {
            reports,
            formulae,
            analysisExcel,
            name: this.analysisName?.trim() || null,
            project: this.selectedProject?._id ?? null,
            dueDate: this.analysisDueDate ? this.analysisDueDate.toISOString() : null,
            milestones: this.analysisMilestones.length > 0 ? this.analysisMilestones : undefined,
            deletedMilestoneIds: [],
            teamMembers: this.analysisShareDetails.users,
            workspaceShare: this.analysisShareDetails.workspaceShare,
            projectShare: this.analysisShareDetails.projectShare,
            setFormulaeAsDefault: this.setFormulaeAsDefault,
            setAnalysisExcelAsDefault: this.setAnalysisExcelAsDefault,
            analysisExcelIsFromDefault: this.analysisExcelIsFromDefault,
            clearFormulaeDefault: this.formulaePrePopulatedFromDefault && !this.setFormulaeAsDefault,
            clearAnalysisExcelDefault: this.analysisExcelPrePopulatedFromDefault && !this.setAnalysisExcelAsDefault,
            projectInfo: {
                projectArea: this.projectAreaValue,
                shiftHoursPerDay: this.shiftHoursPerDayValue,
                theoreticalBudget: this.theoreticalBudgetValue,
                theoreticalScheduleWorkdays: this.theoreticalScheduleWorkdaysValue,
                projectLocation: this.projectLocationValue?.trim() || null,
            },
        };
    }

    onAnalysisAddUserToShare(_shareUser: Partial<IShareUser>): void {
        // Share-to-users component already pushes to shareDetails.users (same reference as analysisShareDetails);
        // do not add again here or the user will appear twice.
    }

    onAnalysisRemoveUserFromShare(shareUser: Partial<IShareUser>): void {
        this.analysisShareDetails.users = this.analysisShareDetails.users.filter(
            (u) => u.email !== shareUser.email
        );
    }

    onAnalysisUpdateSharedUser(shareUser: IShareUser): void {
        this.analysisShareDetails.users = this.analysisShareDetails.users.map((u) =>
            u.email === shareUser.email ? shareUser : u
        );
    }

    onAnalysisChangeSharedWithWorkspace(value: boolean): void {
        this.analysisShareDetails.workspaceShare = value;
    }

    onAnalysisChangeSharedWithProject(value: boolean): void {
        this.analysisShareDetails.projectShare = value;
    }

    onAnalysisChangeAnyoneAccess(access: SHARE_ACCESS): void {
        this.analysisShareDetails.anyone = access;
    }

    /** Get the actual File for a local upload (from .file or file-selection metadata). */
    private getLocalFile(display: LayFoundationFileDisplay): File | undefined {
        if (!display.isLocal) return undefined;
        return display.file ?? (display.fileItem?.metadata?.uploadedFile as File | undefined);
    }

    /**
     * Build FormData for POST /geotechnical-analysis/:
     * - data: JSON string (required)
     * - reportFiles: array (multiple parts with same key) for local reports
     * - formulaFile: single file (one part) for local formulae
     * - analysisExcelFile: single file (one part) for local analysis excel
     */
    private collectLocalFiles(): { fieldName: GeotechnicalUploadFile['fieldName']; file: File }[] {
        const result: { fieldName: GeotechnicalUploadFile['fieldName']; file: File }[] = [];
        for (const f of this.reportsFiles) {
            const file = this.getLocalFile(f);
            if (file) result.push({ fieldName: 'reportFiles', file });
        }
        const formulaFile = this.formulaeFile ? this.getLocalFile(this.formulaeFile) : undefined;
        if (formulaFile) result.push({ fieldName: 'formulaFile', file: formulaFile });
        const analysisExcelFile = this.analysisExcelFile ? this.getLocalFile(this.analysisExcelFile) : undefined;
        if (analysisExcelFile) result.push({ fieldName: 'analysisExcelFile', file: analysisExcelFile });
        return result;
    }

    /**
     * Build payload for Lay the foundation step.
     * Converts local File to ArrayBuffer; for files selected from Workspace/Project/Documents,
     * only local file parts are converted — use fileItem.metadata for backend to resolve by id.
     */
    async getLayFoundationPayload(): Promise<LayFoundationPayload> {
        const fileToArrayBuffer = (file: File): Promise<ArrayBuffer> =>
            new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(r.result as ArrayBuffer);
                r.onerror = reject;
                r.readAsArrayBuffer(file);
            });

        const reportsBuffers: ArrayBuffer[] = [];
        for (const f of this.reportsFiles) {
            if (f.isLocal && f.file) reportsBuffers.push(await fileToArrayBuffer(f.file));
        }
        const formulaeBuffer = this.formulaeFile?.isLocal && this.formulaeFile?.file
            ? await fileToArrayBuffer(this.formulaeFile.file) : null;
        const analysisExcelBuffer = this.analysisExcelFile?.isLocal && this.analysisExcelFile?.file
            ? await fileToArrayBuffer(this.analysisExcelFile.file) : null;

        return {
            reports: reportsBuffers,
            formulae: formulaeBuffer,
            analysisExcel: analysisExcelBuffer,
        };
    }

    onAnalysisPreviousStepClick(): void {
        // Stepper handles previous step
    }

    async onAnalysisFinish(): Promise<void> {
        if (this.savingAnalysis()) return;

        const agentRemaining = this.userCreditsState.agentRemaining();
        if (agentRemaining <= 0) {
            this.toastService.error("You've run out of credits. Request more to continue.");
            return;
        }
        if (agentRemaining < 3) {
            this.toastService.warn(`Low credits (${agentRemaining} remaining). This analysis may not complete.`);
        }

        this.savingAnalysis.set(true);
        const isEdit = this.isEditMode && !!this.editingAnalysisId;

        try {
            const localFiles = this.collectLocalFiles();
            const payload = this.buildSavePayload();

            // Upload local files to S3 via pre-signed URLs
            if (localFiles.length > 0) {
                const uploadFiles: GeotechnicalUploadFile[] = localFiles.map(({ fieldName, file }) => ({
                    fieldName,
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream',
                }));

                const presignedRes = await this.geotechnicalAnalysisService
                    .getUploadPresignedUrls(uploadFiles)
                    .toPromise();

                if (!presignedRes?.success || !presignedRes.data?.urls) {
                    throw new Error('Failed to get upload URLs');
                }

                // Upload each file to S3 and collect s3Keys by fieldName
                const reportFileKeys: string[] = [];
                let formulaFileKey: string | null = null;
                let analysisExcelFileKey: string | null = null;

                await Promise.all(presignedRes.data.urls.map(async (presigned) => {
                    const match = localFiles.find(({ fieldName, file }) =>
                        fieldName === presigned.fieldName && file.name === presigned.fileName
                    );
                    if (!match) return;
                    await this.geotechnicalAnalysisService.uploadToS3(presigned.uploadUrl, match.file).toPromise();
                    if (presigned.fieldName === 'reportFiles') {
                        reportFileKeys.push(presigned.s3Key);
                    } else if (presigned.fieldName === 'formulaFile') {
                        formulaFileKey = presigned.s3Key;
                    } else if (presigned.fieldName === 'analysisExcelFile') {
                        analysisExcelFileKey = presigned.s3Key;
                    }
                }));

                if (reportFileKeys.length) payload.reportFileKeys = reportFileKeys;
                if (formulaFileKey) payload.formulaFileKey = formulaFileKey;
                if (analysisExcelFileKey) payload.analysisExcelFileKey = analysisExcelFileKey;
            }

            const request$ = isEdit
                ? this.geotechnicalAnalysisService.updateAndSave(this.editingAnalysisId!, payload)
                : this.geotechnicalAnalysisService.createAndSave(payload);

            request$.subscribe({
                next: (res) => {
                    this.savingAnalysis.set(false);
                    const validationErrorExcel = (res?.data as any)?.['validationErrorExcel'] as { data: number[] } | undefined;
                    if (validationErrorExcel) {
                        const blob = new Blob([new Uint8Array(validationErrorExcel.data)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        saveAs(blob, 'Analysis-Excel-Errors.xlsx');
                        this.toastService.error('Analysis excel has validation errors. Please check the downloaded file.');
                        return;
                    }
                    if (res?.success && res?.data) {
                        this.loadAnalysisItems();
                        this.onStepperClose();
                        this.toastService.success(isEdit ? 'Geotechnical analysis updated successfully' : 'Geotechnical analysis created. You\'ll be notified once it\'s ready.');
                    } else if ((res as any)?.code === 429) {
                        this.toastService.warn(res?.message ?? 'Max parallel analyses in progress. Please wait for one to complete.');
                    } else {
                        this.toastService.error(res?.message ?? (isEdit ? 'Failed to update analysis' : 'Failed to save analysis'));
                    }
                },
                error: (err) => {
                    this.savingAnalysis.set(false);
                    const msg = err?.error?.message ?? err?.message ?? 'An error occurred while saving';
                    this.toastService.error(msg);
                },
            });

        } catch (err: any) {
            this.savingAnalysis.set(false);
            const msg = err?.message ?? 'An error occurred while uploading files';
            this.toastService.error(msg);
        }
    }

    get isProjectInfoValid(): boolean {
        return (
            this.projectAreaValue !== null && this.projectAreaValue > 0 &&
            this.shiftHoursPerDayValue !== null && this.shiftHoursPerDayValue > 0 &&
            this.theoreticalBudgetValue !== null && this.theoreticalBudgetValue > 0 &&
            this.theoreticalScheduleWorkdaysValue !== null && this.theoreticalScheduleWorkdaysValue > 0
        );
    }

    get shouldInterceptStepperClose(): boolean {
        return !this.isEditMode && (
            this.reportsFiles.length > 0 ||
            this.formulaeFile !== null ||
            this.analysisExcelFile !== null ||
            this.analysisName.trim() !== '' ||
            this.selectedProject !== null ||
            this.analysisDueDate !== null
        );
    }

    onStepperCloseRequest(): void {
        this.showUnsavedChangesDialog = true;
    }

    onUnsavedChangesContinue(): void {
        this.showUnsavedChangesDialog = false;
        const action = this.pendingUnsavedChangesAction;
        this.pendingUnsavedChangesAction = null;
        if (action) {
            action();
        } else {
            this.onStepperClose();
        }
    }

    onUnsavedChangesCancel(): void {
        this.showUnsavedChangesDialog = false;
        this.pendingUnsavedChangesAction = null;
    }

    onStepperClose(): void {
        this.showGenerateAnalysisStepper = false;
        this.isEditMode = false;
        this.editingAnalysisId = null;
        this.resetStepperState();
    }

    private resetStepperState(): void {
        // Step 1 - Lay the foundation
        this.reportsFiles = [];
        this.formulaeFile = null;
        this.analysisExcelFile = null;
        this.setFormulaeAsDefault = false;
        this.setAnalysisExcelAsDefault = false;
        this.analysisExcelIsFromDefault = false;
        this.analysisExcelPrePopulatedFromDefault = false;
        this.formulaePrePopulatedFromDefault = false;
        this.reportsAccordionValue = '0';
        this.formulaeAccordionValue = '0';
        this.analysisExcelAccordionValue = '0';

        // Step 2 - Analysis information
        this.analysisName = '';
        this.selectedProject = null;
        this.analysisDueDate = null;
        this.analysisMilestones = [{ name: 'Milestone 1 - Completion of review' }];
        this.analysisInfoCustomisationAccordionValue = '0';
        this.analysisInfoMilestonesAccordionValue = undefined;
        this.analysisInfoProjectDetailsAccordionValue = '0';
        this.projectAreaValue = null;
        this.shiftHoursPerDayValue = null;
        this.theoreticalBudgetValue = null;
        this.theoreticalScheduleWorkdaysValue = null;
        this.projectLocationValue = '';

        // Step 3 - Team share
        this.analysisShareDetails = {
            users: [],
            anyone: SHARE_ACCESS.RESTRICTED,
            workspaceShare: false,
            projectShare: false,
        };

        // Reset stepper to first step
        this.stepperService.reset();
    }

    onSearchAnalysisName(): void {
        this.first = 0;
        this.loadAnalysisItems(0, this.rows);
    }

    onGenerateAnalysisExcel(): void {
        this.showGenerateAnalysisStepper = false;
        this.openGenerateExcelSidebar();
    }

    openGenerateExcelSidebar(): void {
        this.openConfigurationsDrawer();
    }

    loadGeneratedAnalysisExcels(): void {
        this.loadingGenerateExcelAgreements = true;
        const pendingToggle$ = this.pendingPreferenceUpdate$ ?? of(null);
        pendingToggle$.pipe(
            catchError(() => of(null)),
            switchMap(() => this.geotechnicalAnalysisService.getUserPreferences())
        ).subscribe({
            next: (prefRes) => {
                const prefData = prefRes?.success && prefRes?.data ? prefRes.data as import('../../services/geotechnical-analysis.service').UserGeotechnicalPreference : null;
                this.defaultAnalysisExcelAttachmentId = prefData?.defaultAnalysisExcel?.attachmentId ?? null;

                this.geotechnicalAnalysisService
                    .listGeneratedAnalysisExcels({ skip: 0, limit: 50 })
                    .subscribe({
                        next: (res) => {
                            this.loadingGenerateExcelAgreements = false;
                            const data = res?.success && res?.data ? (res.data as { items?: GeneratedAnalysisExcelListItem[]; total?: number }) : null;
                            const items = data?.items ?? [];
                            this.generateExcelAgreementItems = items.map((item) =>
                                this.mapGeneratedExcelListItemToSidebarItem(item)
                            );
                        },
                        error: () => {
                            this.loadingGenerateExcelAgreements = false;
                            this.generateExcelAgreementItems = [];
                        },
                    });
            },
            error: () => {
                this.geotechnicalAnalysisService
                    .listGeneratedAnalysisExcels({ skip: 0, limit: 50 })
                    .subscribe({
                        next: (res) => {
                            this.loadingGenerateExcelAgreements = false;
                            const data = res?.success && res?.data ? (res.data as { items?: GeneratedAnalysisExcelListItem[]; total?: number }) : null;
                            this.generateExcelAgreementItems = (data?.items ?? []).map((item) =>
                                this.mapGeneratedExcelListItemToSidebarItem(item)
                            );
                        },
                        error: () => {
                            this.loadingGenerateExcelAgreements = false;
                            this.generateExcelAgreementItems = [];
                        },
                    });
            },
        });
    }

    private mapGeneratedExcelListItemToSidebarItem(
        item: GeneratedAnalysisExcelListItem
    ): GenerateExcelAgreementItem {
        const docs = item.documents ?? [];
        const types = docs.map((d) => (d.type ?? '').toLowerCase());
        const hasPdf = types.some((t) => t.includes('pdf'));
        const hasDoc = types.some((t) => t.includes('word') || t.includes('doc') || t.includes('msword'));
        const hasExcel = types.some((t) => t.includes('excel') || t.includes('xlsx') || t.includes('spreadsheet'));
        const analysisAttachmentId = item.analysis?._id ?? null;
        return {
            id: item._id ?? '',
            name: item.name ?? 'Unnamed',
            selected: !!analysisAttachmentId && analysisAttachmentId === this.defaultAnalysisExcelAttachmentId,
            fileCount: docs.length,
            hasPdf,
            hasDoc,
            hasExcel,
            source: item,
        };
    }

    toggleAgreementSelection(item: GenerateExcelAgreementItem): void {
        const newSelected = !item.selected;
        const analysisAttachmentId = item.source?.analysis?._id ?? null;

        if (newSelected && !analysisAttachmentId) {
            this.toastService.error('This excel is still being generated. Try again once it finishes.');
            return;
        }

        const previousItems = this.generateExcelAgreementItems;
        const previousDefaultId = this.defaultAnalysisExcelAttachmentId;
        this.defaultAnalysisExcelAttachmentId = newSelected ? analysisAttachmentId : null;
        this.generateExcelAgreementItems = this.generateExcelAgreementItems.map((i) => ({
            ...i,
            selected: newSelected ? i.id === item.id : false,
        }));

        const prefUpdate: { defaultAnalysisExcel: GeoPreferenceInputRef | null } = {
            defaultAnalysisExcel: newSelected && analysisAttachmentId
                ? { attachment: analysisAttachmentId, source: 'LOCAL' }
                : null,
        };

        const request$ = this.geotechnicalAnalysisService.updateUserPreferences(prefUpdate).pipe(
            finalize(() => { this.pendingPreferenceUpdate$ = null; }),
            shareReplay(1)
        );
        this.pendingPreferenceUpdate$ = request$;

        request$.subscribe({
            next: (res) => {
                if (res?.success === false) {
                    this.defaultAnalysisExcelAttachmentId = previousDefaultId;
                    this.generateExcelAgreementItems = previousItems;
                    this.toastService.error('Failed to update default analysis excel');
                }
            },
            error: () => {
                this.defaultAnalysisExcelAttachmentId = previousDefaultId;
                this.generateExcelAgreementItems = previousItems;
                this.toastService.error('Failed to update default analysis excel');
            },
        });
    }

    onGenerateExcelSidebarPrimaryAction(): void {
        this.showGenerateExcelSidebar = false;
        this.openFileSelectionFor('generateAnalysisExcel');
    }

    closeGenerateExcelSidebar(): void {
        this.showGenerateExcelSidebar = false;
        this.generateExcelSidebarView = 'list';
        this.seeAllFilesSelectedItem = null;
        this.seeAllFilesSearchQuery = '';
    }

    onSeeAllAgreementFiles(item: GenerateExcelAgreementItem): void {
        this.seeAllFilesSelectedItem = item;
        this.seeAllFilesSearchQuery = '';
        this.generateExcelSidebarView = 'files';
        this.showConfigurationsDrawer = false;
        this.showGenerateExcelSidebar = true;
    }

    closeUploadedFilesDialog(): void {
        this.uploadedFilesDialogVisible = false;
        this.seeAllFilesSelectedItem = null;
        this.seeAllFilesSearchQuery = '';
    }

    onAgreementMenuButtonClick(event: Event, item: GenerateExcelAgreementItem, menu: MenuComponent): void {
        this.selectedAgreementMenuItem = item;
        menu.toggle(event);
    }

    onAgreementMenuAction(menuItem: CustomMenuItem): void {
        const item = this.selectedAgreementMenuItem;
        if (!item) return;
        if (menuItem.label === 'View') {
            this.onAgreementMenuView(item);
        } else if (menuItem.label === 'Rename') {
            this.onAgreementMenuRename(item);
        } else if (menuItem.label === 'Delete') {
            this.onAgreementMenuDelete(item);
        }
    }

    /** Agreement row menu: View → call backend get-analysis-file; open in OnlyOffice editor or show toast. */
    onAgreementMenuView(item: GenerateExcelAgreementItem): void {
        this.geotechnicalAnalysisService.getAnalysisFile(item.id).subscribe((res) => {
            if (res?.success && res?.data) {
                const data = res.data as GetAnalysisFileResponse;
                if (data?.status === 'IN_PROGRESS') {
                    this.toastService.warn('Analysis is still in progress');
                } else if (data?.status === 'CREDITS_EXHAUSTED') {
                    this.toastService.error("Credits exhausted — analysis generation paused. Top up credits to continue.");
                } else if (data?.status === 'FAILED') {
                    this.toastService.error('Failed to load analysis file');
                } else if (data?.awsSignedUrl) {
                    this.openAnalysisExcelEditor(item, data.awsSignedUrl);
                }
            } else {
                this.toastService.error('Failed to load analysis file');
            }
        });
    }

    private openAnalysisExcelEditor(item: GenerateExcelAgreementItem, signedUrl: string): void {
        const currentUser = this.authService.getUser();
        const tokenConfig: Partial<IConfig> = {
            document: {
                fileType: 'xlsx',
                key: item.id + '-' + Math.random().toString(36).substring(2, 15),
                title: item.name,
                url: signedUrl,
                permissions: {
                    download: true,
                    edit: false,
                    print: true,
                },
            },
            documentType: ONLY_OFFICE_EDITOR_TYPE.EXCEL,
            editorConfig: {
                user: {
                    id: currentUser?.email,
                    name: [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') || currentUser?.email,
                },
                mode: 'view',
            },
            height: '100%',
            width: '100%',
        };
        const token = this.jwtService.signToken(tokenConfig, environment.jwtSecret);
        this.analysisExcelEditor = { ...tokenConfig, token };
        this.analysisExcelPreview = item;
        this.analysisExcelPreviewVisible = true;
        this.showGenerateExcelSidebar = false;
    }

    closeAnalysisExcelPreview(): void {
        this.analysisExcelPreviewVisible = false;
        this.analysisExcelPreview = null;
        this.analysisExcelEditor = {};
    }

    /** Agreement row menu: Delete → open confirmation dialog. */
    onAgreementMenuDelete(item: GenerateExcelAgreementItem, popover?: PopoverComponent): void {
        popover?.hide();
        this.deleteAgreementTarget = item;
        this.deleteAgreementDialogVisible = true;
    }

    /** Confirmed delete → call backend then remove from list. */
    onDeleteAgreementConfirm(): void {
        const item = this.deleteAgreementTarget;
        if (!item) return;
        this.deletingAgreement = true;
        this.geotechnicalAnalysisService.deleteAnalysis(item.id).subscribe({
            next: (res) => {
                this.deletingAgreement = false;
                this.deleteAgreementDialogVisible = false;
                if (res?.success) {
                    this.generateExcelAgreementItems = this.generateExcelAgreementItems.filter((i) => i.id !== item.id);
                    this.deleteAgreementTarget = null;
                }
            },
            error: () => {
                this.deletingAgreement = false;
                this.toastService.error('Failed to delete analysis');
            },
        });
    }

    /** Agreement row menu: Rename → open rename dialog. */
    onAgreementMenuRename(item: GenerateExcelAgreementItem, popover?: PopoverComponent): void {
        popover?.hide();
        this.renameAgreementTarget = item;
        this.renameAgreementValue = item.name;
        this.renameAgreementDialogVisible = true;
    }

    /** Confirm rename dialog → call editExcelAnalysis with new name. */
    onRenameAgreementConfirm(): void {
        const item = this.renameAgreementTarget;
        const newName = this.renameAgreementValue.trim();
        if (!item || !newName) return;
        this.renamingAgreement = true;
        this.geotechnicalAnalysisService.editExcelAnalysis({ id: item.id, name: newName }).subscribe({
            next: (res) => {
                this.renamingAgreement = false;
                this.renameAgreementDialogVisible = false;
                const target = this.generateExcelAgreementItems.find((i) => i.id === item.id);
                if (target) target.name = newName;
                this.renameAgreementTarget = null;
            },
            error: () => {
                this.renamingAgreement = false;
                this.toastService.error('Failed to rename');
            },
        });
    }

    onSeeAllFilesBack(): void {
        this.showGenerateExcelSidebar = false;
        this.generateExcelSidebarView = 'list';
        this.seeAllFilesSelectedItem = null;
        this.seeAllFilesSearchQuery = '';
        this.showConfigurationsDrawer = true;
    }

    /** Documents for the "See all" file list, filtered by search. */
    get seeAllFilteredDocuments(): { _id: string; name: string; size?: number }[] {
        const item = this.seeAllFilesSelectedItem;
        const docs = item?.source?.documents ?? [];
        const q = (this.seeAllFilesSearchQuery ?? '').trim().toLowerCase();
        if (!q) return docs;
        return docs.filter((d) => (d.name ?? '').toLowerCase().includes(q));
    }

    onGridViewClick(): void {
        // Stub: toggle grid view layout
    }

    onCalendarViewClick(): void {
        // Stub: toggle calendar / date view
    }

    onSortClick(): void {
        // Stub: open sort options
    }

    onFilterClick(): void {
        // Stub: open filter panel
    }

    onStartAnalysis(item: GeotechnicalAnalysisItem): void {
        // Stub: start or resume analysis for this item
    }

    onCardHistoryClick(item: GeotechnicalAnalysisItem): void {
        // Stub: show history/timeline for this analysis
    }

    onCardQuickActionClick(item: GeotechnicalAnalysisItem): void {
        // Stub: quick action for this analysis
    }

    onCardMenuClick(event: Event, item: GeotechnicalAnalysisItem): void {
        this.selectedCardItem = item;
        this.cardMenu.toggle(event);
    }

    onCardMenuButtonClick(event: Event, item: GeotechnicalAnalysisItem, menu: MenuComponent): void {
        if (this.currentlyOpenCardMenu && this.currentlyOpenCardMenu !== menu) {
            this.currentlyOpenCardMenu.hide();
        }
        this.selectedCardItem = item;
        this.updateCardMenuItemsForRole(item.currentUserRole);
        this.currentlyOpenCardMenu = menu;
        menu.toggle(event);
    }

    onCardMenuAction(payload: CustomMenuItem | { item?: MenuItem }): void {
        const menuItem = payload && 'label' in payload ? payload : (payload as { item?: MenuItem })?.item;
        if (!menuItem || !this.selectedCardItem) return;
        const label = (menuItem as CustomMenuItem).label ?? (menuItem as MenuItem).label;
        if (label === 'Delete') {
            this.showDeleteDialog = true;
        } else if (label === 'Edit') {
            this.onEditAnalysis();
        } else if (label === 'Rename') {
            this.renameCardValue = this.selectedCardItem.reportName;
            this.renameCardDialogVisible = true;
        } else if (label === 'Due date') {
            this.cardDueDateValue = this.selectedCardItem.dueDate ? new Date(this.selectedCardItem.dueDate) : null;
            this.cardDueDateDialogVisible = true;
        }
    }

    onEditAnalysis(): void {
        if (!this.selectedCardItem) return;
        this.geotechnicalAnalysisService.getReport(this.selectedCardItem.id).subscribe({
            next: (res) => {
                if (!res?.success || !res?.data) {
                    this.toastService.error('Failed to load analysis details');
                    return;
                }
                const report = res.data;
                this.isEditMode = true;
                this.editingAnalysisId = report._id;
                this.prefillEditFormData(report);
                this.currentStepIndex.set(0);
                this.stepperService.setCurrentStep(0);
                this.showGenerateAnalysisStepper = true;
            },
            error: () => {
                this.toastService.error('Failed to load analysis details');
            },
        });
    }

    /** Card rename dialog: confirm → call updateAnalysisName. */
    onRenameCardConfirm(): void {
        const item = this.selectedCardItem;
        const newName = this.renameCardValue.trim();
        if (!item || !newName) return;
        this.renamingCard = true;
        this.geotechnicalAnalysisService.updateAnalysisName(item.id, newName).subscribe({
            next: () => {
                this.renamingCard = false;
                this.renameCardDialogVisible = false;
                const target = this.analysisItems.find((i) => i.id === item.id);
                if (target) target.reportName = newName;
                this.toastService.success("Renamed analysis successfully")
            },
            error: () => {
                this.renamingCard = false;
                this.toastService.error('Failed to rename analysis');
            },
        });
    }

    /** Card set-due-date dialog: confirm → call updateAnalysisDueDate. */
    onSetCardDueDateConfirm(): void {
        const item = this.selectedCardItem;
        if (!item) return;
        this.settingCardDueDate = true;
        this.geotechnicalAnalysisService.updateAnalysisDueDate(item.id, this.cardDueDateValue).subscribe({
            next: () => {
                this.settingCardDueDate = false;
                this.cardDueDateDialogVisible = false;
                const target = this.analysisItems.find((i) => i.id === item.id);
                if (target) target.dueDate = this.cardDueDateValue?.toISOString() ?? undefined;
            },
            error: () => {
                this.settingCardDueDate = false;
                this.toastService.error('Failed to update due date');
            },
        });
    }

    private prefillEditFormData(report: GeotechnicalAnalysisReport): void {
        this.analysisName = report.name ?? '';
        this.analysisDueDate = report.dueDate ? new Date(report.dueDate) : null;

        // Load projects and match selected project by ID
        this.projectService.getProjects(0, 500, {}).subscribe((projRes) => {
            if (projRes?.success && projRes?.data?.projects) {
                this.analysisProjects = projRes.data.projects;
                this.analysisProjectOptions = this.analysisProjects.map((p) => ({
                    label: p.name || '',
                    value: p,
                }));
                if (report.project) {
                    const projectId = typeof report.project === 'object' ? (report.project as IProject)._id : report.project as string;
                    this.selectedProject = this.analysisProjects.find((p) => p._id === projectId) ?? null;
                } else {
                    this.selectedProject = null;
                }
            }
        });

        // Milestones
        const rawMilestones = (report as any).milestones;
        if (rawMilestones?.length) {
            this.analysisMilestones = rawMilestones.map((m: any) => ({
                ...m,
                date: m.date ? new Date(m.date) : new Date(new Date().setHours(23, 59, 59, 999)),
            }));
        }

        // Report files (Step 1)
        this.reportsFiles = (report.reports ?? []).map((r) => {
            const name = r.name ?? r.file?.name ?? r.attachment?.filename ?? 'Report';
            const id = r.file?._id ?? r.attachment?._id ?? r._id ?? '';
            return {
                id,
                name,
                sourceLabel: 'Documents',
                isLocal: false,
                fileItem: { id, name } as FileItem,
            };
        });

        // Formulae file
        const formulae = report.formulae;
        this.formulaeFile = formulae
            ? (() => {
                const name = formulae.file?.name ?? formulae.attachment?.filename ?? 'Formulae';
                const id = formulae.file?._id ?? formulae.attachment?._id ?? '';
                return {
                    id,
                    name,
                    sourceLabel: 'Documents',
                    isLocal: false,
                    fileItem: { id, name } as FileItem,
                };
            })()
            : null;

        // Analysis excel file
        const analysisExcel = report.analysisExcel;
        this.analysisExcelFile = analysisExcel
            ? (() => {
                const name = analysisExcel.file?.name ?? analysisExcel.attachment?.filename ?? 'Analysis Excel';
                const id = analysisExcel.file?._id ?? analysisExcel.attachment?._id ?? '';
                return {
                    id,
                    name,
                    sourceLabel: 'Documents',
                    isLocal: false,
                    fileItem: { id, name } as FileItem,
                };
            })()
            : null;

        // Team members
        if (report.teamMembers?.length) {
            this.analysisShareDetails.users = report.teamMembers.map((m) => ({
                _id: m._id,
                user: m.user,
                email: m.email ?? '',
                name: m.name ?? '',
                type: USER_TYPE.INTERNAL,
                role: (m as any).role ?? SHARE_ACCESS.VIEW,
            } as IShareUser));
        } else {
            this.setCurrentUserAsOwner();
        }
        this.analysisShareDetails.workspaceShare = (report as any).workspaceShare ?? false;
        this.analysisShareDetails.projectShare = (report as any).projectShare ?? false;
        this.analysisShareDetails.anyone = (report as any).shareAccess ?? SHARE_ACCESS.RESTRICTED;

        // Project info fields (from vendor analysis)
        this.geotechnicalAnalysisService.getVendorAnalysis(report._id).subscribe({
            next: (vaRes) => {
                if (vaRes?.success && vaRes?.data?.projectInfo) {
                    const pi = vaRes.data.projectInfo;
                    this.projectAreaValue = typeof pi.projectArea?.value === 'number' ? pi.projectArea.value : null;
                    this.shiftHoursPerDayValue = typeof pi.shiftHoursPerDay?.value === 'number' ? pi.shiftHoursPerDay.value : null;
                    this.theoreticalBudgetValue = typeof pi.theoreticalBudget?.value === 'number' ? pi.theoreticalBudget.value : null;
                    this.theoreticalScheduleWorkdaysValue = typeof pi.theoreticalScheduleWorkdays?.value === 'number' ? pi.theoreticalScheduleWorkdays.value : null;
                    this.projectLocationValue = typeof pi.projectLocation?.value === 'string' ? pi.projectLocation.value : '';
                }
            },
        });
    }

    confirmDeleteAnalysis(): void {
        if (!this.selectedCardItem) return;
        this.deletingAnalysis = true;
        this.geotechnicalAnalysisService.deleteAnalysisReport(this.selectedCardItem.id).subscribe({
            next: (res) => {
                this.deletingAnalysis = false;
                this.showDeleteDialog = false;
                if (res.success) {
                    this.toastService.success(res.message ?? 'Analysis deleted successfully');
                    this.selectedCardItem = null;
                    this.loadAnalysisItems();
                } else {
                    this.toastService.error(res.message ?? 'Failed to delete analysis');
                }
            },
            error: () => {
                this.deletingAnalysis = false;
                this.toastService.error('Failed to delete analysis');
            },
        });
    }

    downloadSample(type: GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE) {
        if (this.downloadingSample) return;
        this.downloadingSample = true;
        const FILE_META: Record<GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE, { name: string; mime: string }> = {
            [GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE.EXAMPLE_EXCEL]: { name: 'Example Analysis Excel.xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
            [GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE.STANDARD_EXCEL]: { name: 'Standard Analysis Excel.xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
            [GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE.EXAMPLE_FORMULA]: { name: 'Example Formula.docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
            [GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE.STANDARD_FORMULA]: { name: 'Standard Formula.docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
        };
        const { name, mime } = FILE_META[type];
        this.geotechnicalAnalysisService.downloadSample(type).subscribe({
            next: (res) => {
                this.downloadingSample = false;
                if (res.success && res.data) {
                    const blob = new Blob([new Uint8Array(res.data.data)], { type: mime });
                    saveAs(blob, name);
                }
            },
            error: () => {
                this.downloadingSample = false;
                this.toastService.error(`Failed to download ${name}`);
            },
        });
    }
}
